import React, {
  useState,
  useEffect,
  useMemo,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { Expense, Income, Debt } from "../lib/types";
import { expenseUtils, incomeUtils, formatDateUTC } from "../lib";
import { getCurrentMonthReceivedIncomeTotal } from "../lib/transactionIncomeUtils";
import { plaidService } from "../services/external/plaidService";
import {
  expenseAPI,
  incomeAPI,
  dashboardAPI,
  isAuthTransitionError,
  Dashboard,
  DashboardSection as APIDashboardSection,
} from "../services/api/api";
import debtDB from "../services/database/debtDB";
import incomeDB from "../services/database/incomeDB";
import IncomeForm from "../components/income/IncomeForm";
import { plaidCreditCardService } from "../services/plaid/plaidCreditCardService";
import {
  plaidBankAccountService,
  BankAccountBalance,
} from "../services/plaid/plaidBankAccountService";
import { isAbortError } from "../services/utils/abort";
import {
  Tune,
  ExpandMore as ExpandMoreIcon,
  DragIndicator,
  Add as AddIcon,
  Delete as DeleteIcon,
  Palette as PaletteIcon,
  ViewColumn as ViewColumnIcon,
  Sync as SyncIcon,
  InfoOutlined as InfoOutlinedIcon,
  Edit as EditIcon,
  PauseCircleOutline as PauseCircleOutlineIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
} from "@mui/icons-material";
import { TpcInvoiceLink } from "../components/income/TpcInvoiceLink";
import {
  Card,
  CardContent,
  Typography,
  Box,
  useTheme,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Tabs,
  Tab,
  Popover,
  Tooltip,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  UniqueIdentifier,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DashboardTabs from "../components/dashboard/DashboardTabs";
import CreateDashboardDialog, {
  DashboardTemplate,
} from "../components/dashboard/CreateDashboardDialog";
import ShareDashboardModal from "../components/dashboard/ShareDashboardModal";
import { useShareAction } from "../contexts/ShareActionContext";
import {
  fetchDailyAverages,
  upsertDailyAverage,
} from "../services/database/incomeProjectionDB";
import { useAuth } from "../contexts/AuthContext";

interface DashboardPageProps {
  setPageTitle?: (title: string) => void;
}

// Dashboard section and card types for drag-and-drop
interface DashboardCard {
  id: string;
  label: string;
}

interface DashboardSection {
  id: string;
  label: string;
  cards: DashboardCard[];
}

// Default sections configuration
const DEFAULT_SECTIONS: DashboardSection[] = [
  {
    id: "this-month",
    label: "This Month",
    cards: [
      { id: "checking-balance", label: "Checking balance" },
      { id: "total-income", label: "Income (expected/received)" },
      { id: "remaining-income", label: "Remaining income" },
      { id: "total-expenses", label: "Total expenses" },
      { id: "remaining-expenses", label: "Remaining expenses" },
      { id: "savings-balance", label: "Savings balance" },
      { id: "net-cash-flow", label: "Net cash flow" },
      { id: "credit-utilization", label: "Credit utilization" },
    ],
  },
  {
    id: "next-month",
    label: "Next Month",
    cards: [
      { id: "next-month-expenses", label: "Expenses" },
      { id: "next-month-income", label: "Income" },
      { id: "next-month-debt-payments", label: "Debt payments" },
    ],
  },
  {
    id: "debt-overview",
    label: "Debt Overview",
    cards: [
      { id: "monthly-debt-payments", label: "Monthly debt payments" },
      { id: "personal-debts", label: "Personal debts" },
      { id: "business-debts", label: "Business debts" },
      { id: "credit-cards", label: "Credit cards" },
    ],
  },
  {
    id: "expected-next-30",
    label: "Within 30 Days",
    cards: [
      { id: "expected-expenses", label: "Expenses" },
      { id: "expected-income", label: "Income" },
      { id: "upcoming-debt-payments", label: "Upcoming debt payments" },
    ],
  },
  {
    id: "next-10-days",
    label: "Within 10 Days",
    cards: [
      { id: "next-10-expenses", label: "Expenses" },
      { id: "next-10-income", label: "Income" },
      { id: "next-10-debt-payments", label: "Debt payments" },
    ],
  },
  {
    id: "next-7-days",
    label: "Within 7 Days",
    cards: [
      { id: "next-7-expenses", label: "Expenses" },
      { id: "next-7-income", label: "Income" },
      { id: "next-7-debt-payments", label: "Debt payments" },
    ],
  },
  {
    id: "annual-overview",
    label: "Annually",
    cards: [
      { id: "current-year-income", label: "Current year income" },
      { id: "projected-income-2026", label: "Projected income 2026" },
      { id: "current-year-expenses", label: "Current year expenses" },
      { id: "past-year-income", label: "Past year income" },
      { id: "past-year-expenses", label: "Past year expenses" },
    ],
  },
];

const LAYOUT_STORAGE_KEY = "dashboard-layout";
const CARD_COLORS_STORAGE_KEY = "dashboard-card-colors";
const SECTION_COLUMNS_STORAGE_KEY = "dashboard-section-columns";
const COLORING_ENABLED_STORAGE_KEY = "dashboard-coloring-enabled";

// Column options for sections (number of columns per row)
const COLUMN_OPTIONS = [1, 2, 3, 4, 5, 6];

// Default colors for each card
const DEFAULT_CARD_COLORS: Record<string, string> = {
  "remaining-expenses": "#E53E3E",
  "remaining-income": "#38A169",
  "net-cash-flow": "#3182CE",
  "total-expenses": "#E53E3E",
  "total-income": "#3182CE",
  "credit-utilization": "#3182CE",
  "checking-balance": "#38A169",
  "savings-balance": "#38A169",
  "monthly-debt-payments": "#E53E3E",
  "personal-debts": "#E53E3E",
  "business-debts": "#E53E3E",
  "credit-cards": "#E53E3E",
  "expected-expenses": "#E53E3E",
  "expected-income": "#38A169",
  "upcoming-debt-payments": "#E53E3E",
  "next-10-expenses": "#E53E3E",
  "next-10-income": "#38A169",
  "next-10-debt-payments": "#E53E3E",
  "next-7-expenses": "#E53E3E",
  "next-7-income": "#38A169",
  "next-7-debt-payments": "#E53E3E",
  "next-month-expenses": "#E53E3E",
  "next-month-income": "#38A169",
  "next-month-debt-payments": "#E53E3E",
  "current-year-income": "#38A169",
  "projected-income-2026": "#38A169",
  "current-year-expenses": "#E53E3E",
  "past-year-income": "#38A169",
  "past-year-expenses": "#E53E3E",
};

const removeCashflowSection = (
  layout: DashboardSection[]
): DashboardSection[] => {
  return layout.filter((section) => section.id !== "cashflow");
};

const insertMissingProjectedIncomeCard = (
  layout: DashboardSection[]
): DashboardSection[] => {
  return layout.map((section) => {
    if (section.id !== "annual-overview") return section;
    const hasCard = section.cards.some(
      (card) => card.id === "projected-income-2026"
    );
    if (hasCard) return section;
    const insertAfterIndex = section.cards.findIndex(
      (card) => card.id === "current-year-income"
    );
    const insertIndex =
      insertAfterIndex === -1 ? section.cards.length : insertAfterIndex + 1;
    const nextCards = [...section.cards];
    nextCards.splice(insertIndex, 0, {
      id: "projected-income-2026",
      label: "Projected income 2026",
    });
    return { ...section, cards: nextCards };
  });
};

const insertMissingNextDaySections = (
  layout: DashboardSection[]
): DashboardSection[] => {
  const hasNextTen = layout.some((section) => section.id === "next-10-days");
  const hasNextSeven = layout.some((section) => section.id === "next-7-days");
  if (hasNextTen && hasNextSeven) return layout;

  const nextTenSection = DEFAULT_SECTIONS.find(
    (section) => section.id === "next-10-days"
  );
  const nextSevenSection = DEFAULT_SECTIONS.find(
    (section) => section.id === "next-7-days"
  );
  if (!nextTenSection && !nextSevenSection) return layout;

  const insertAfterIndex = layout.findIndex(
    (section) => section.id === "expected-next-30"
  );
  let insertIndex =
    insertAfterIndex === -1 ? layout.length : insertAfterIndex + 1;
  let updated = [...layout];

  if (!hasNextTen && nextTenSection) {
    updated = [
      ...updated.slice(0, insertIndex),
      nextTenSection,
      ...updated.slice(insertIndex),
    ];
    insertIndex += 1;
  }

  if (!hasNextSeven && nextSevenSection) {
    updated = [
      ...updated.slice(0, insertIndex),
      nextSevenSection,
      ...updated.slice(insertIndex),
    ];
  }

  return updated;
};

// Color palette for color picker
const COLOR_PALETTE = [
  "#E53E3E", // Red
  "#DD6B20", // Orange
  "#D69E2E", // Yellow
  "#38A169", // Green
  "#319795", // Teal
  "#3182CE", // Blue
  "#805AD5", // Purple
  "#D53F8C", // Pink
  "#718096", // Gray
];

export interface BreakdownItem {
  name: string;
  amount: number;
  date?: Date | string;
  type?: string;
  isReceived?: boolean;
  // Carried through from the source income so income rows can link back to
  // their origin (e.g. a TPC Account Center invoice).
  sourceProvider?: string;
  sourceDocumentId?: string;
  sourceDocumentType?: string;
  sourceClientId?: string;
  // Set for rows that map back to a Focus: Finance-native record so the breakdown can
  // offer edit / delete / suspend. Absent for derived or imported rows.
  entityId?: string;
  entityType?: "income";
  // Set when part of the record has already been collected: `amount` is the
  // remaining balance, `grossAmount` the original value.
  grossAmount?: number;
  paidToDate?: number;
  isSuspended?: boolean;
  suspendedUntil?: Date | string | null;
}

// Default end date offered when suspending: one month out, in YYYY-MM-DD for
// the date input. Clearing the field in the dialog suspends indefinitely.
const defaultSuspendUntil = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().slice(0, 10);
};

// Actions a breakdown row or group header can offer for Focus: Finance-native
// records. Delete and suspend take a list so a vendor header can act on every
// occurrence in its group at once; a single row just passes itself.
export interface BreakdownItemActions {
  onEdit: (item: BreakdownItem) => void;
  onDelete: (items: BreakdownItem[]) => void;
  onSuspend: (items: BreakdownItem[]) => void;
}

interface BalanceBreakdownItem {
  name: string;
  officialName?: string | null;
  currentBalance: number;
  availableBalance: number | null;
  lastSynced: Date;
  institution?: string | null;
  mask?: string;
  itemId?: string;
  isPlaidConnected?: boolean;
}

interface CreditUtilizationItem {
  name: string;
  balance: number;
  creditLimit: number;
  utilization: number;
}

interface GroupedBreakdownItem {
  groupName: string;
  items: BreakdownItem[];
  totalAmount: number;
}

interface NestedGroupedBreakdownItem {
  categoryName: string; // "Income" or "Expenses"
  vendors: GroupedBreakdownItem[];
  totalAmount: number;
}

// Helper function to group items by type
const groupBreakdownItemsByType = (
  items: BreakdownItem[]
): GroupedBreakdownItem[] => {
  const groups = new Map<string, BreakdownItem[]>();

  items.forEach((item) => {
    const groupName = item.type || "Uncategorized";
    if (!groups.has(groupName)) {
      groups.set(groupName, []);
    }
    groups.get(groupName)!.push(item);
  });

  const result: GroupedBreakdownItem[] = [];
  groups.forEach((groupItems, groupName) => {
    result.push({
      groupName,
      items: groupItems.sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)),
      totalAmount: groupItems.reduce((sum, item) => sum + item.amount, 0),
    });
  });

  return result.sort(
    (a, b) => Math.abs(b.totalAmount) - Math.abs(a.totalAmount)
  );
};

// Helper function to group items by vendor, nested under Income/Expenses categories
const groupBreakdownItemsByVendor = (
  items: BreakdownItem[]
): NestedGroupedBreakdownItem[] => {
  // Separate into income and expenses
  const incomeItems = items.filter((item) => item.amount >= 0);
  const expenseItems = items.filter((item) => item.amount < 0);

  // Helper to group items by vendor name
  const groupByVendor = (
    categoryItems: BreakdownItem[]
  ): GroupedBreakdownItem[] => {
    const groups = new Map<string, BreakdownItem[]>();

    categoryItems.forEach((item) => {
      const separatorIndex = item.name.indexOf(" - ");
      const groupName =
        separatorIndex > 0
          ? item.name.substring(0, separatorIndex).trim()
          : item.name;

      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)!.push(item);
    });

    const result: GroupedBreakdownItem[] = [];
    groups.forEach((groupItems, groupName) => {
      result.push({
        groupName,
        items: groupItems.sort(
          (a, b) => Math.abs(b.amount) - Math.abs(a.amount)
        ),
        totalAmount: groupItems.reduce((sum, item) => sum + item.amount, 0),
      });
    });

    return result.sort(
      (a, b) => Math.abs(b.totalAmount) - Math.abs(a.totalAmount)
    );
  };

  const result: NestedGroupedBreakdownItem[] = [];

  if (incomeItems.length > 0) {
    result.push({
      categoryName: "Income",
      vendors: groupByVendor(incomeItems),
      totalAmount: incomeItems.reduce((sum, item) => sum + item.amount, 0),
    });
  }

  if (expenseItems.length > 0) {
    result.push({
      categoryName: "Expenses",
      vendors: groupByVendor(expenseItems),
      totalAmount: expenseItems.reduce((sum, item) => sum + item.amount, 0),
    });
  }

  return result;
};

// Helper interface and function to group debts by creditor for Highest Balances display
interface GroupedDebt {
  creditor: string;
  debts: Debt[];
  totalBalance: number;
}

const groupDebtsByCreditor = (debts: Debt[]): GroupedDebt[] => {
  const groups = new Map<string, Debt[]>();

  debts.forEach((debt) => {
    const creditor = debt.creditor || debt.account || "Unknown";
    if (!groups.has(creditor)) {
      groups.set(creditor, []);
    }
    groups.get(creditor)!.push(debt);
  });

  const result: GroupedDebt[] = [];
  groups.forEach((groupDebts, creditor) => {
    result.push({
      creditor,
      debts: groupDebts.sort(
        (a, b) =>
          Math.abs(Number(b.balance || 0)) - Math.abs(Number(a.balance || 0))
      ),
      totalBalance: groupDebts.reduce(
        (sum, debt) => sum + Math.abs(Number(debt.balance || 0)),
        0
      ),
    });
  });

  return result.sort((a, b) => b.totalBalance - a.totalBalance);
};

// Hover-reveal component for grouped debt items (no layout shift)
const DebtGroupHoverReveal: React.FC<{
  group: GroupedDebt;
  isDarkMode: boolean;
  formatCurrency: (amount: number) => string;
}> = ({ group, isDarkMode, formatCurrency }) => {
  const [isHovered, setIsHovered] = useState(false);
  const hasMultipleDebts = group.debts.length > 1;

  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: "relative",
        cursor: hasMultipleDebts ? "pointer" : "default",
      }}
    >
      {/* Main row - always visible */}
      <Typography
        variant="body2"
        sx={{
          color: isDarkMode ? "#a1a1aa" : "#71717a",
          fontSize: "0.75rem",
          mt: 0.25,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>
          {group.creditor}
          {hasMultipleDebts && (
            <span
              style={{
                marginLeft: 4,
                fontSize: "0.65rem",
                opacity: 0.7,
              }}
            >
              ({group.debts.length})
            </span>
          )}
        </span>
        <span>-{formatCurrency(group.totalBalance)}</span>
      </Typography>

      {/* Expandable items - slide down on hover */}
      {hasMultipleDebts && (
        <Box
          sx={{
            maxHeight: isHovered ? `${group.debts.length * 24}px` : "0px",
            overflow: "hidden",
            transition: "max-height 0.25s ease-in-out",
            pl: 1.5,
            borderLeft: isHovered
              ? `2px solid ${isDarkMode ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)"}`
              : "2px solid transparent",
            ml: 0.5,
          }}
        >
          {group.debts.map((debt, idx) => (
            <Typography
              key={idx}
              variant="caption"
              sx={{
                color: isDarkMode ? "#71717a" : "#a1a1aa",
                fontSize: "0.7rem",
                display: "flex",
                justifyContent: "space-between",
                py: 0.25,
              }}
            >
              <span>{debt.account || debt.creditor}</span>
              <span>
                -{formatCurrency(Math.abs(Number(debt.balance || 0)))}
              </span>
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

// Modal component for showing grouped breakdown items with tabs and accordions
type GroupingMode = "single" | "type" | "vendor";

export const BreakdownModal: React.FC<{
  open: boolean;
  onClose: () => void;
  items: BreakdownItem[];
  title: string;
  formatCurrency: (amount: number) => string;
  itemActions?: BreakdownItemActions;
}> = ({ open, onClose, items, title, formatCurrency, itemActions }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [groupingMode, setGroupingMode] = useState<GroupingMode>("vendor");
  const [expanded, setExpanded] = useState<string | false>(false);
  const [expandedVendor, setExpandedVendor] = useState<string | false>(false);

  // Get grouped items based on selected mode (for type and single modes)
  const displayItems = useMemo(() => {
    // Sort items by amount (descending) for all modes
    const sortedItems = [...items].sort(
      (a, b) => Math.abs(b.amount) - Math.abs(a.amount)
    );

    switch (groupingMode) {
      case "type":
        return groupBreakdownItemsByType(sortedItems);
      case "single":
      default:
        // For single items, wrap each in its own group
        return sortedItems.map((item) => ({
          groupName: item.name,
          items: [item],
          totalAmount: item.amount,
        }));
    }
  }, [items, groupingMode]);

  // Get vendor-grouped items (nested structure)
  const vendorDisplayItems = useMemo(() => {
    const sortedItems = [...items].sort(
      (a, b) => Math.abs(b.amount) - Math.abs(a.amount)
    );
    return groupBreakdownItemsByVendor(sortedItems);
  }, [items]);

  const handleAccordionChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const handleVendorAccordionChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedVendor(isExpanded ? panel : false);
    };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: GroupingMode
  ) => {
    setGroupingMode(newValue);
    setExpanded(false);
    setExpandedVendor(false);
  };

  const totalAmount = items.reduce((sum, item) => sum + item.amount, 0);

  // Edit / suspend / delete controls. Only rendered for Focus: Finance-native records
  // — rows imported from TPC Account Center are managed upstream and get the
  // deep-link icon instead.
  const isManageable = (item: BreakdownItem) =>
    Boolean(itemActions && item.entityId && item.sourceProvider !== "tpc");

  // Shared by leaf rows and vendor headers. `targets` is the set the action
  // applies to: one row, or every manageable occurrence under a header.
  // Editing is only offered when a single record is unambiguously in scope.
  const renderActions = (
    targets: BreakdownItem[],
    opts: { iconSize?: number } = {}
  ) => {
    const manageable = targets.filter(isManageable);
    if (manageable.length === 0 || !itemActions) return null;

    const fontSize = opts.iconSize ?? 15;
    const iconSx = {
      p: 0.25,
      color: isDarkMode ? "#9ca3af" : "#6b7280",
      "&:hover": { color: isDarkMode ? "#e5e7eb" : "#111827" },
    };
    // A header whose occurrences are all suspended offers Resume; otherwise
    // Suspend, which then covers any still-active ones.
    const allSuspended = manageable.every((item) => item.isSuspended);
    const many = manageable.length > 1;
    const scope = many ? ` all ${manageable.length}` : "";

    return (
      <>
        {manageable.length === 1 && (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              sx={iconSx}
              onClick={(e) => {
                e.stopPropagation();
                itemActions.onEdit(manageable[0]);
              }}
            >
              <EditIcon sx={{ fontSize }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title={`${allSuspended ? "Resume" : "Suspend"}${scope}`}>
          <IconButton
            size="small"
            sx={iconSx}
            onClick={(e) => {
              e.stopPropagation();
              itemActions.onSuspend(manageable);
            }}
          >
            {allSuspended ? (
              <PlayCircleOutlineIcon sx={{ fontSize }} />
            ) : (
              <PauseCircleOutlineIcon sx={{ fontSize }} />
            )}
          </IconButton>
        </Tooltip>
        <Tooltip title={`Delete${scope}`}>
          <IconButton
            size="small"
            sx={{ ...iconSx, "&:hover": { color: "#E53E3E" } }}
            onClick={(e) => {
              e.stopPropagation();
              itemActions.onDelete(manageable);
            }}
          >
            <DeleteIcon sx={{ fontSize }} />
          </IconButton>
        </Tooltip>
      </>
    );
  };

  const renderItemActions = (item: BreakdownItem) => renderActions([item]);

  // The amount that counts, with the record's full value beneath it whenever
  // part of it has already been collected — so a row still reconciles against
  // the source system instead of only showing the balance.
  const renderAmount = (
    item: BreakdownItem,
    variant?: "body2",
    fontSize?: string
  ) => (
    <Box sx={{ textAlign: "right" }}>
      <Typography
        variant={variant}
        sx={{
          fontWeight: 600,
          ...(fontSize ? { fontSize } : {}),
          whiteSpace: "nowrap",
          color: item.amount >= 0 ? "#10b981" : "#E53E3E",
        }}
      >
        {formatCurrency(item.amount)}
      </Typography>
      {typeof item.grossAmount === "number" && (item.paidToDate ?? 0) > 0 && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            whiteSpace: "nowrap",
            fontSize: "0.68rem",
            color: isDarkMode ? "#6b7280" : "#9ca3af",
          }}
        >
          {`${formatCurrency(item.grossAmount)} total · ${formatCurrency(
            item.paidToDate ?? 0
          )} paid`}
        </Typography>
      )}
    </Box>
  );

  // Render a single item row
  const renderSingleItem = (item: BreakdownItem, key: string | number) => (
    <Box
      key={key}
      sx={{
        backgroundColor: isDarkMode ? "#262626" : "#f9fafb",
        mb: 1,
        borderRadius: "8px",
        p: 2,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
        opacity: item.isSuspended ? 0.55 : 1,
        boxShadow: isDarkMode
          ? "0 1px 3px rgba(0,0,0,0.3)"
          : "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              color: isDarkMode ? "#ffffff" : "#111827",
              textDecoration: item.isSuspended ? "line-through" : "none",
            }}
          >
            {item.name}
          </Typography>
          {item.date && (
            <Typography
              variant="caption"
              sx={{
                color: isDarkMode ? "#6b7280" : "#9ca3af",
                whiteSpace: "nowrap",
              }}
            >
              {formatDateUTC(item.date)}
            </Typography>
          )}
          <TpcInvoiceLink
            sourceProvider={item.sourceProvider}
            sourceDocumentId={item.sourceDocumentId}
            sourceDocumentType={item.sourceDocumentType}
            sourceClientId={item.sourceClientId}
          />
          {item.isReceived && (
            <Typography
              variant="caption"
              sx={{
                backgroundColor: "#38A16920",
                color: "#38A169",
                px: 1,
                py: 0.25,
                borderRadius: "4px",
                fontWeight: 600,
                fontSize: "11px",
              }}
            >
              Received
            </Typography>
          )}
          {item.isSuspended && (
            <Typography
              variant="caption"
              sx={{
                backgroundColor: "#D69E2E20",
                color: "#D69E2E",
                px: 1,
                py: 0.25,
                borderRadius: "4px",
                fontWeight: 600,
                fontSize: "11px",
                whiteSpace: "nowrap",
              }}
            >
              {item.suspendedUntil
                ? `Suspended to ${formatDateUTC(item.suspendedUntil)}`
                : "Suspended"}
            </Typography>
          )}
        </Box>
        {item.type && (
          <Typography
            variant="caption"
            sx={{
              color: isDarkMode ? "#6b7280" : "#9ca3af",
              display: "block",
            }}
          >
            {item.type}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
        {renderItemActions(item)}
        {renderAmount(item)}
      </Box>
    </Box>
  );

  // Render a group accordion
  const renderGroupAccordion = (group: GroupedBreakdownItem, index: number) => (
    <Accordion
      key={group.groupName}
      expanded={expanded === `panel-${index}`}
      onChange={handleAccordionChange(`panel-${index}`)}
      sx={{
        backgroundColor: isDarkMode ? "#262626" : "#f9fafb",
        "&:before": { display: "none" },
        mb: 1,
        borderRadius: "8px !important",
        overflow: "hidden",
        boxShadow: isDarkMode
          ? "0 1px 3px rgba(0,0,0,0.3)"
          : "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ color: isDarkMode ? "#9ca3af" : "#6b7280" }} />
        }
        sx={{
          minHeight: 38,
          "&.Mui-expanded": { minHeight: 38 },
          "& .MuiAccordionSummary-content": {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mr: 1,
            my: 0.375,
            "&.Mui-expanded": { my: 0.375 },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 600,
              color: isDarkMode ? "#ffffff" : "#111827",
            }}
          >
            {group.groupName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              backgroundColor: isDarkMode ? "#374151" : "#e5e7eb",
              px: 1,
              py: 0.25,
              borderRadius: "12px",
              color: isDarkMode ? "#9ca3af" : "#6b7280",
            }}
          >
            {group.items.length} items
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          {renderActions(group.items)}
          <Typography
            sx={{
              fontWeight: 600,
              whiteSpace: "nowrap",
              color: group.totalAmount >= 0 ? "#10b981" : "#E53E3E",
            }}
          >
            {formatCurrency(group.totalAmount)}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          backgroundColor: isDarkMode ? "#1f1f1f" : "#ffffff",
          borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
          p: 0,
        }}
      >
        {group.items.map((item, itemIndex) => (
          <Box
            key={itemIndex}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 0.75,
              px: 2,
              gap: 1,
              opacity: item.isSuspended ? 0.55 : 1,
              borderBottom:
                itemIndex < group.items.length - 1
                  ? `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`
                  : "none",
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: isDarkMode ? "#e5e7eb" : "#374151",
                    textDecoration: item.isSuspended ? "line-through" : "none",
                  }}
                >
                  {item.name}
                </Typography>
                {item.date && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDarkMode ? "#6b7280" : "#9ca3af",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatDateUTC(item.date)}
                  </Typography>
                )}
                <TpcInvoiceLink
                  sourceProvider={item.sourceProvider}
                  sourceDocumentId={item.sourceDocumentId}
                  sourceDocumentType={item.sourceDocumentType}
                  sourceClientId={item.sourceClientId}
                />
                {item.isReceived && (
                  <Typography
                    variant="caption"
                    sx={{
                      backgroundColor: "#38A16920",
                      color: "#38A169",
                      px: 0.75,
                      py: 0.125,
                      borderRadius: "4px",
                      fontWeight: 600,
                      fontSize: "10px",
                    }}
                  >
                    Received
                  </Typography>
                )}
                {item.isSuspended && (
                  <Typography
                    variant="caption"
                    sx={{
                      backgroundColor: "#D69E2E20",
                      color: "#D69E2E",
                      px: 0.75,
                      py: 0.125,
                      borderRadius: "4px",
                      fontWeight: 600,
                      fontSize: "10px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item.suspendedUntil
                      ? `Suspended to ${formatDateUTC(item.suspendedUntil)}`
                      : "Suspended"}
                  </Typography>
                )}
              </Box>
              {item.type && groupingMode !== "type" && (
                <Typography
                  variant="caption"
                  sx={{
                    color: isDarkMode ? "#6b7280" : "#9ca3af",
                    display: "block",
                  }}
                >
                  {item.type}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              {renderItemActions(item)}
              {renderAmount(item, "body2")}
            </Box>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );

  // Render nested vendor accordions (Income/Expenses -> Vendors -> Items)
  const renderVendorCategory = (
    category: NestedGroupedBreakdownItem,
    categoryIndex: number
  ) => (
    <Accordion
      key={category.categoryName}
      expanded={expanded === `category-${categoryIndex}`}
      onChange={handleAccordionChange(`category-${categoryIndex}`)}
      sx={{
        backgroundColor: isDarkMode ? "#2d2d2d" : "#f3f4f6",
        "&:before": { display: "none" },
        mb: 1,
        borderRadius: "8px !important",
        overflow: "hidden",
        boxShadow: isDarkMode
          ? "0 1px 3px rgba(0,0,0,0.3)"
          : "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ color: isDarkMode ? "#9ca3af" : "#6b7280" }} />
        }
        sx={{
          minHeight: 38,
          "&.Mui-expanded": { minHeight: 38 },
          "& .MuiAccordionSummary-content": {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mr: 1,
            my: 0.375,
            "&.Mui-expanded": { my: 0.375 },
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "1rem",
              color: isDarkMode ? "#ffffff" : "#111827",
            }}
          >
            {category.categoryName}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              backgroundColor: isDarkMode ? "#374151" : "#e5e7eb",
              px: 1,
              py: 0.25,
              borderRadius: "12px",
              color: isDarkMode ? "#9ca3af" : "#6b7280",
            }}
          >
            {category.vendors.length} vendors
          </Typography>
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            color: category.totalAmount >= 0 ? "#10b981" : "#E53E3E",
          }}
        >
          {formatCurrency(category.totalAmount)}
        </Typography>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          backgroundColor: isDarkMode ? "#1f1f1f" : "#ffffff",
          borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
          p: 1,
        }}
      >
        {category.vendors.map((vendor, vendorIndex) => (
          <Accordion
            key={vendor.groupName}
            expanded={
              expandedVendor ===
              `category-${categoryIndex}-vendor-${vendorIndex}`
            }
            onChange={handleVendorAccordionChange(
              `category-${categoryIndex}-vendor-${vendorIndex}`
            )}
            sx={{
              backgroundColor: isDarkMode ? "#262626" : "#f9fafb",
              "&:before": { display: "none" },
              mb: 0.5,
              borderRadius: "6px !important",
              overflow: "hidden",
              boxShadow: "none",
            }}
          >
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon
                  sx={{
                    color: isDarkMode ? "#9ca3af" : "#6b7280",
                    fontSize: 18,
                  }}
                />
              }
              sx={{
                minHeight: 32,
                "&.Mui-expanded": { minHeight: 32 },
                "& .MuiAccordionSummary-content": {
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mr: 1,
                  my: 0.25,
                  "&.Mui-expanded": { my: 0.25 },
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: isDarkMode ? "#e5e7eb" : "#374151",
                  }}
                >
                  {vendor.groupName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: isDarkMode ? "#374151" : "#e5e7eb",
                    px: 0.75,
                    py: 0.125,
                    borderRadius: "10px",
                    color: isDarkMode ? "#9ca3af" : "#6b7280",
                    fontSize: "0.7rem",
                  }}
                >
                  {vendor.items.length}
                </Typography>
                {/* Deep-link on the group header itself, taken from the first
                    Account Center row in the group. */}
                {(() => {
                  const tpcItem = vendor.items.find(
                    (i) => i.sourceProvider === "tpc" && i.sourceDocumentId
                  );
                  if (!tpcItem) return null;
                  return (
                    <Box
                      component="span"
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      sx={{ display: "inline-flex" }}
                    >
                      <TpcInvoiceLink
                        sourceProvider={tpcItem.sourceProvider}
                        sourceDocumentId={tpcItem.sourceDocumentId}
                        sourceDocumentType={tpcItem.sourceDocumentType}
                        sourceClientId={tpcItem.sourceClientId}
                      />
                    </Box>
                  );
                })()}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                {renderActions(vendor.items)}
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    whiteSpace: "nowrap",
                    color: vendor.totalAmount >= 0 ? "#10b981" : "#E53E3E",
                  }}
                >
                  {formatCurrency(vendor.totalAmount)}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
                borderTop: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                p: 0,
              }}
            >
              {vendor.items.map((item, itemIndex) => (
                <Box
                  key={itemIndex}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    py: 0.5,
                    px: 2,
                    opacity: item.isSuspended ? 0.55 : 1,
                    borderBottom:
                      itemIndex < vendor.items.length - 1
                        ? `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`
                        : "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: isDarkMode ? "#d1d5db" : "#4b5563",
                        fontSize: "0.8rem",
                        textDecoration: item.isSuspended
                          ? "line-through"
                          : "none",
                      }}
                    >
                      {item.name}
                    </Typography>
                    {item.date && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDarkMode ? "#6b7280" : "#9ca3af",
                          fontSize: "0.7rem",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatDateUTC(item.date)}
                      </Typography>
                    )}
                    <TpcInvoiceLink
                      sourceProvider={item.sourceProvider}
                      sourceDocumentId={item.sourceDocumentId}
                      sourceDocumentType={item.sourceDocumentType}
                      sourceClientId={item.sourceClientId}
                    />
                    {item.isReceived && (
                      <Typography
                        variant="caption"
                        sx={{
                          backgroundColor: "#38A16920",
                          color: "#38A169",
                          px: 0.5,
                          py: 0.125,
                          borderRadius: "4px",
                          fontWeight: 600,
                          fontSize: "9px",
                        }}
                      >
                        Received
                      </Typography>
                    )}
                    {item.isSuspended && (
                      <Typography
                        variant="caption"
                        sx={{
                          backgroundColor: "#D69E2E20",
                          color: "#D69E2E",
                          px: 0.5,
                          py: 0.125,
                          borderRadius: "4px",
                          fontWeight: 600,
                          fontSize: "9px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.suspendedUntil
                          ? `Suspended to ${formatDateUTC(item.suspendedUntil)}`
                          : "Suspended"}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
                  >
                    {renderItemActions(item)}
                    {renderAmount(item, "body2", "0.8rem")}
                  </Box>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
          backgroundImage: isDarkMode
            ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
            : "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: isDarkMode ? "#9ca3af" : "#6b7280" }}
          >
            {items.length} items · Total: {formatCurrency(totalAmount)}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Grouping Tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: isDarkMode ? "rgba(255,255,255,0.1)" : "divider",
        }}
      >
        <Tabs
          value={groupingMode}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              minHeight: 40,
              fontSize: "0.8rem",
              textTransform: "none",
              color: isDarkMode ? "#9ca3af" : "#6b7280",
              "&.Mui-selected": {
                color: isDarkMode ? "#ffffff" : "#111827",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: isDarkMode ? "#3b82f6" : "#2563eb",
            },
          }}
        >
          <Tab label="By Vendor" value="vendor" />
          <Tab label="By Type" value="type" />
          <Tab label="Single Items" value="single" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2, pb: 3 }}>
        {items.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: isDarkMode ? "#9ca3af" : "#6b7280", py: 2 }}
          >
            No items to display
          </Typography>
        ) : groupingMode === "vendor" ? (
          // Vendor mode - render nested accordions (Income/Expenses -> Vendors)
          vendorDisplayItems.map((category, index) =>
            renderVendorCategory(category, index)
          )
        ) : groupingMode === "single" ? (
          // Single items mode - render flat list
          displayItems.map((group, index) =>
            renderSingleItem(group.items[0], index)
          )
        ) : (
          // Type mode - render accordions
          displayItems.map((group, index) => renderGroupAccordion(group, index))
        )}
      </DialogContent>
    </Dialog>
  );
};

// Balance Breakdown Modal - shows detailed account info with Available vs Current balance
const BalanceBreakdownModal: React.FC<{
  open: boolean;
  onClose: () => void;
  items: BalanceBreakdownItem[];
  title: string;
  formatCurrency: (amount: number) => string;
  onSync?: () => Promise<{ success: boolean; message: string }>;
}> = ({ open, onClose, items, title, formatCurrency, onSync }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{
    type: "success" | "error" | "warning";
    text: string;
  } | null>(null);

  // Check if any accounts are Plaid connected
  const hasPlaidAccounts = items.some((item) => item.isPlaidConnected);

  // Handle sync click
  const handleSync = async () => {
    if (!onSync) return;

    setSyncing(true);
    setSyncMessage(null);

    try {
      const result = await onSync();
      if (result.success) {
        setSyncMessage({ type: "success", text: result.message });
        // Close message after 3 seconds on success
        setTimeout(() => setSyncMessage(null), 3000);
      } else {
        setSyncMessage({ type: "warning", text: result.message });
      }
    } catch (error) {
      setSyncMessage({
        type: "error",
        text: error instanceof Error ? error.message : "Sync failed",
      });
    } finally {
      setSyncing(false);
    }
  };

  // Calculate totals
  const totalCurrentBalance = items.reduce(
    (sum, item) => sum + item.currentBalance,
    0
  );
  const totalAvailableBalance = items.reduce(
    (sum, item) => sum + (item.availableBalance ?? item.currentBalance),
    0
  );
  const totalPendingBalance = totalCurrentBalance - totalAvailableBalance;
  const hasPendingDifference = totalPendingBalance !== 0;

  // Find the most recent sync time
  const lastSyncedDate =
    items.length > 0
      ? new Date(
          Math.max(...items.map((item) => new Date(item.lastSynced).getTime()))
        )
      : null;

  const balanceExplanation = `Current Balance: The total amount of money in your account, including pending transactions that haven't cleared yet.

Available Balance: The amount you can actually spend right now. This excludes pending transactions and any holds on your account.

The difference between them represents money that's "on hold" - typically pending transactions, recent deposits that haven't cleared, or bank-placed holds.`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
          backgroundImage: isDarkMode
            ? "linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))"
            : "none",
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: isDarkMode
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.1)",
          pb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: isDarkMode ? "#ffffff" : "#111827",
            }}
          >
            {title}
          </Typography>
          {lastSyncedDate && (
            <Typography
              variant="caption"
              sx={{
                color: isDarkMode ? "#6b7280" : "#9ca3af",
                display: "block",
                mt: 0.5,
              }}
            >
              Last updated: {lastSyncedDate.toLocaleString()}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {hasPlaidAccounts && onSync && (
            <Tooltip title="Sync accounts with Plaid" arrow>
              <span>
                <IconButton
                  onClick={handleSync}
                  size="small"
                  disabled={syncing}
                  sx={{
                    color: syncing
                      ? isDarkMode
                        ? "#4b5563"
                        : "#9ca3af"
                      : isDarkMode
                        ? "#3b82f6"
                        : "#2563eb",
                    animation: syncing ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                >
                  <SyncIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: isDarkMode ? "#9ca3af" : "#6b7280" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3 }}>
        {/* Sync Status Message */}
        {syncMessage && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: "8px",
              backgroundColor:
                syncMessage.type === "success"
                  ? isDarkMode
                    ? "rgba(34, 197, 94, 0.15)"
                    : "rgba(34, 197, 94, 0.1)"
                  : syncMessage.type === "warning"
                    ? isDarkMode
                      ? "rgba(234, 179, 8, 0.15)"
                      : "rgba(234, 179, 8, 0.1)"
                    : isDarkMode
                      ? "rgba(239, 68, 68, 0.15)"
                      : "rgba(239, 68, 68, 0.1)",
              border: `1px solid ${
                syncMessage.type === "success"
                  ? "#22c55e"
                  : syncMessage.type === "warning"
                    ? "#eab308"
                    : "#ef4444"
              }`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color:
                  syncMessage.type === "success"
                    ? "#22c55e"
                    : syncMessage.type === "warning"
                      ? "#eab308"
                      : "#ef4444",
                fontWeight: 500,
              }}
            >
              {syncMessage.text}
            </Typography>
          </Box>
        )}
        {/* Balance Type Legend with Tooltip */}
        <Tooltip
          title={
            <Typography sx={{ whiteSpace: "pre-line", fontSize: "0.85rem" }}>
              {balanceExplanation}
            </Typography>
          }
          arrow
          placement="bottom"
          enterDelay={200}
          leaveDelay={200}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 4,
              mb: 2,
              p: 1.5,
              backgroundColor: isDarkMode ? "#262626" : "#f3f4f6",
              borderRadius: "8px",
              cursor: "help",
              "&:hover": {
                backgroundColor: isDarkMode ? "#333333" : "#e5e7eb",
              },
            }}
          >
            {/* Pending - leftmost (only show if different) */}
            {hasPendingDifference && (
              <Box sx={{ textAlign: "right", minWidth: 80 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: isDarkMode ? "#9ca3af" : "#6b7280",
                    textTransform: "uppercase",
                    fontSize: "0.65rem",
                    letterSpacing: "0.05em",
                  }}
                >
                  Pending
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: "#DD6B20",
                  }}
                >
                  {formatCurrency(totalPendingBalance)}
                </Typography>
              </Box>
            )}
            {/* Available - middle */}
            <Box sx={{ textAlign: "right", minWidth: 80 }}>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  textTransform: "uppercase",
                  fontSize: "0.65rem",
                  letterSpacing: "0.05em",
                }}
              >
                Available
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: "#38A169",
                }}
              >
                {formatCurrency(totalAvailableBalance)}
              </Typography>
            </Box>
            {/* Current - rightmost */}
            <Box sx={{ textAlign: "right", minWidth: 80 }}>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  textTransform: "uppercase",
                  fontSize: "0.65rem",
                  letterSpacing: "0.05em",
                }}
              >
                Current
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: isDarkMode ? "#ffffff" : "#111827",
                }}
              >
                {formatCurrency(totalCurrentBalance)}
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                right: 24,
                top: 80,
                color: isDarkMode ? "#6b7280" : "#9ca3af",
                fontSize: "0.65rem",
              }}
            >
              Hover for details
            </Typography>
          </Box>
        </Tooltip>

        {/* Individual Accounts */}
        {items.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: isDarkMode ? "#9ca3af" : "#6b7280", py: 2 }}
          >
            No accounts found
          </Typography>
        ) : (
          items.map((account, index) => (
            <Box
              key={index}
              sx={{
                backgroundColor: isDarkMode ? "#262626" : "#f9fafb",
                mb: 1.5,
                borderRadius: "8px",
                p: 2,
                boxShadow: isDarkMode
                  ? "0 1px 3px rgba(0,0,0,0.3)"
                  : "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 3,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: isDarkMode ? "#ffffff" : "#111827",
                    }}
                  >
                    {account.officialName || account.name}
                  </Typography>
                  {account.institution && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDarkMode ? "#6b7280" : "#9ca3af",
                        display: "block",
                      }}
                    >
                      {account.institution}
                      {account.mask && ` ••••${account.mask}`}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDarkMode ? "#6b7280" : "#9ca3af",
                      fontSize: "0.7rem",
                      display: "block",
                    }}
                  >
                    {new Date(account.lastSynced).toLocaleString()}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 4,
                    flexWrap: "wrap",
                  }}
                >
                  {/* Pending - leftmost (only show if different) */}
                  {account.availableBalance !== null &&
                    account.availableBalance !== account.currentBalance && (
                      <Tooltip
                        title="Difference due to pending transactions or holds"
                        arrow
                      >
                        <Box
                          sx={{
                            textAlign: "right",
                            cursor: "help",
                            minWidth: 80,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: isDarkMode ? "#9ca3af" : "#6b7280",
                              textTransform: "uppercase",
                              fontSize: "0.65rem",
                            }}
                          >
                            Pending
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: "#DD6B20",
                            }}
                          >
                            {formatCurrency(
                              account.currentBalance - account.availableBalance
                            )}
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}
                  {/* Available - middle */}
                  <Tooltip title="Amount available to spend right now" arrow>
                    <Box
                      sx={{ textAlign: "right", cursor: "help", minWidth: 80 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDarkMode ? "#9ca3af" : "#6b7280",
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                        }}
                      >
                        Available
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "#38A169",
                        }}
                      >
                        {account.availableBalance !== null
                          ? formatCurrency(account.availableBalance)
                          : "—"}
                      </Typography>
                    </Box>
                  </Tooltip>

                  {/* Current - rightmost */}
                  <Tooltip
                    title="Total balance including pending transactions"
                    arrow
                  >
                    <Box
                      sx={{ textAlign: "right", cursor: "help", minWidth: 80 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: isDarkMode ? "#9ca3af" : "#6b7280",
                          textTransform: "uppercase",
                          fontSize: "0.65rem",
                        }}
                      >
                        Current
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: isDarkMode ? "#ffffff" : "#111827",
                        }}
                      >
                        {formatCurrency(account.currentBalance)}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </DialogContent>
    </Dialog>
  );
};

// Balance Metric Card - shows accounts on hover, detailed modal on click
const BalanceMetricCard: React.FC<{
  title: string;
  value: string | number;
  color: string;
  borderColor: string;
  accounts: BalanceBreakdownItem[];
  breakdownTitle: string;
  onSync?: () => Promise<{ success: boolean; message: string }>;
}> = ({
  title,
  value,
  color,
  borderColor,
  accounts,
  breakdownTitle,
  onSync,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [modalOpen, setModalOpen] = useState(false);

  const hasAccounts = accounts && accounts.length > 0;

  // Build hover tooltip content showing account names
  const hoverContent = hasAccounts
    ? accounts
        .map(
          (acc) =>
            `${acc.officialName || acc.name}: ${expenseUtils.formatCurrency(acc.currentBalance)}`
        )
        .join("\n")
    : "No accounts connected";

  const handleCardClick = () => {
    if (hasAccounts) {
      setModalOpen(true);
    }
  };

  return (
    <>
      <Tooltip
        title={
          <Typography sx={{ whiteSpace: "pre-line", fontSize: "0.85rem" }}>
            {hoverContent}
          </Typography>
        }
        arrow
        placement="top"
        enterDelay={300}
      >
        <Card
          onClick={handleCardClick}
          sx={{
            borderLeft: `4px solid ${borderColor}`,
            height: "100%",
            backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
            backgroundImage: isDarkMode
              ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
              : "none",
            boxShadow: isDarkMode
              ? "0 4px 6px 0 rgba(0, 0, 0, 0.3), 0 1px 3px 0 rgba(0, 0, 0, 0.2)"
              : "0 4px 6px 0 rgba(0, 0, 0, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.08)",
            transition: "all 0.3s ease",
            cursor: hasAccounts ? "pointer" : "default",
            "&:hover": {
              boxShadow: isDarkMode
                ? "0 8px 12px 0 rgba(0, 0, 0, 0.4), 0 2px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 8px 12px 0 rgba(0, 0, 0, 0.15), 0 2px 6px 0 rgba(0, 0, 0, 0.1)",
              transform: hasAccounts ? "translateY(-2px)" : "none",
            },
          }}
        >
          <CardContent>
            <Typography
              variant="subtitle2"
              sx={{
                color: isDarkMode ? "#9ca3af" : "#6b7280",
                fontWeight: 500,
                letterSpacing: "0.025em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                marginBottom: 1,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: color,
                fontWeight: 700,
                fontSize: "1.875rem",
                lineHeight: 1.2,
              }}
            >
              {value}
            </Typography>
          </CardContent>
        </Card>
      </Tooltip>

      <BalanceBreakdownModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        items={accounts}
        title={breakdownTitle}
        formatCurrency={expenseUtils.formatCurrency}
        onSync={onSync}
      />
    </>
  );
};

// Helper component for dashboard metric cards with dark mode support
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  valueLabel?: string;
  secondaryLabel?: string;
  secondaryValue?: string | number;
  splitValues?: boolean;
  compactValues?: boolean;
  color?: string;
  borderColor: string;
  subtitle?: string;
  footerHint?: string;
  footerHintTooltip?: string;
  breakdownItems?: BreakdownItem[];
  breakdownTitle?: string;
  breakdownItemActions?: BreakdownItemActions;
  onClick?: () => void;
}> = ({
  title,
  value,
  valueLabel,
  secondaryLabel,
  secondaryValue,
  splitValues = false,
  compactValues = false,
  color,
  borderColor,
  subtitle,
  footerHint,
  footerHintTooltip,
  breakdownItems,
  breakdownTitle,
  breakdownItemActions,
  onClick,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [modalOpen, setModalOpen] = useState(false);

  const hasBreakdown = breakdownItems && breakdownItems.length > 0;
  const isClickable = Boolean(onClick || hasBreakdown);

  const handleCardClick = () => {
    if (onClick) {
      onClick();
      return;
    }
    if (hasBreakdown) setModalOpen(true);
  };

  const cardContent = (
    <Card
      onClick={handleCardClick}
      sx={{
        borderLeft: `4px solid ${borderColor}`,
        height: "100%",
        backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
        backgroundImage: isDarkMode
          ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
          : "none",
        boxShadow: isDarkMode
          ? "0 4px 6px 0 rgba(0, 0, 0, 0.3), 0 1px 3px 0 rgba(0, 0, 0, 0.2)"
          : "0 4px 6px 0 rgba(0, 0, 0, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.08)",
        transition: "all 0.3s ease",
        cursor: isClickable ? "pointer" : "default",
        "&:hover": {
          boxShadow: isDarkMode
            ? "0 8px 12px 0 rgba(0, 0, 0, 0.4), 0 2px 6px 0 rgba(0, 0, 0, 0.3)"
            : "0 8px 12px 0 rgba(0, 0, 0, 0.15), 0 2px 6px 0 rgba(0, 0, 0, 0.1)",
          transform: isClickable ? "translateY(-2px)" : "none",
        },
      }}
    >
      <CardContent>
        <Typography
          variant="subtitle2"
          sx={{
            color: isDarkMode ? "#9ca3af" : "#6b7280",
            fontWeight: 500,
            letterSpacing: "0.025em",
            textTransform: "uppercase",
            fontSize: "0.75rem",
            marginBottom: 1,
          }}
        >
          {title}
        </Typography>
        {secondaryLabel && secondaryValue !== undefined ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: splitValues ? "1fr 1fr" : "1fr",
              gap: splitValues ? 2 : 0,
              mt: valueLabel ? 0.5 : 0,
            }}
          >
            <Box>
              {valueLabel && (
                <Typography
                  variant="caption"
                  sx={{
                    color: isDarkMode ? "#6b7280" : "#9ca3af",
                    fontSize: "0.7rem",
                    letterSpacing: "0.03em",
                    textTransform: "uppercase",
                  }}
                >
                  {valueLabel}
                </Typography>
              )}
              <Typography
                variant="h5"
                sx={{
                  color: color || (isDarkMode ? "#ffffff" : "#111827"),
                  fontWeight: 700,
                  fontSize: compactValues ? "1rem" : "1.5rem",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {value}
              </Typography>
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? "#6b7280" : "#9ca3af",
                  fontSize: "0.7rem",
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                }}
              >
                {secondaryLabel}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: color || (isDarkMode ? "#ffffff" : "#111827"),
                  fontWeight: 700,
                  fontSize: compactValues ? "1rem" : "1.5rem",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {secondaryValue}
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            {valueLabel && (
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? "#6b7280" : "#9ca3af",
                  fontSize: "0.7rem",
                  letterSpacing: "0.03em",
                  textTransform: "uppercase",
                }}
              >
                {valueLabel}
              </Typography>
            )}
            <Typography
              variant="h5"
              sx={{
                color: color || (isDarkMode ? "#ffffff" : "#111827"),
                fontWeight: 700,
                fontSize: compactValues ? "1.25rem" : "1.875rem",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {value}
            </Typography>
          </>
        )}
        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              color: isDarkMode ? "#6b7280" : "#9ca3af",
              fontSize: "0.75rem",
              marginTop: 0.5,
              display: "block",
            }}
          >
            {subtitle}
          </Typography>
        )}
        {footerHint && (
          <Box
            sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}
          >
            {footerHintTooltip && (
              <Tooltip title={footerHintTooltip}>
                <IconButton
                  size="small"
                  sx={{
                    p: 0.25,
                    color: isDarkMode ? "#6b7280" : "#9ca3af",
                  }}
                >
                  <InfoOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
            <Typography
              variant="caption"
              sx={{
                color: isDarkMode ? "#6b7280" : "#9ca3af",
                fontSize: "0.75rem",
              }}
            >
              {footerHint}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (hasBreakdown) {
    return (
      <>
        {cardContent}
        <BreakdownModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          items={breakdownItems}
          title={breakdownTitle || title}
          formatCurrency={expenseUtils.formatCurrency}
          itemActions={breakdownItemActions}
        />
      </>
    );
  }

  return cardContent;
};

// Sortable Card Wrapper Component
interface SortableCardWrapperProps {
  id: string;
  children: React.ReactNode;
  isDraggingEnabled: boolean;
  cardColor?: string;
  onColorChange?: (cardId: string, color: string) => void;
}

const SortableCardWrapper: React.FC<SortableCardWrapperProps> = ({
  id,
  children,
  isDraggingEnabled,
  cardColor,
  onColorChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !isDraggingEnabled });

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [colorAnchorEl, setColorAnchorEl] = useState<HTMLElement | null>(null);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative" as const,
  };

  const handleColorClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setColorAnchorEl(event.currentTarget);
  };

  const handleColorClose = () => {
    setColorAnchorEl(null);
  };

  const handleColorSelect = (color: string) => {
    if (onColorChange) {
      onColorChange(id, color);
    }
    handleColorClose();
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ height: "100%", width: "100%" }}>
      {isDraggingEnabled && (
        <Box
          sx={{
            position: "absolute",
            top: 4,
            right: 4,
            zIndex: 10,
            display: "flex",
            gap: "4px",
          }}
        >
          {/* Color Picker Button */}
          <Box
            onClick={handleColorClick}
            sx={{
              cursor: "pointer",
              padding: "4px",
              borderRadius: "4px",
              backgroundColor: isDarkMode
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)",
              "&:hover": {
                backgroundColor: isDarkMode
                  ? "rgba(255,255,255,0.2)"
                  : "rgba(0,0,0,0.1)",
              },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PaletteIcon
              sx={{
                fontSize: 16,
                color: cardColor || (isDarkMode ? "#9ca3af" : "#6b7280"),
              }}
            />
          </Box>
          {/* Drag Handle */}
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: "grab",
              padding: "4px",
              borderRadius: "4px",
              backgroundColor: isDarkMode
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.05)",
              "&:hover": {
                backgroundColor: isDarkMode
                  ? "rgba(255,255,255,0.2)"
                  : "rgba(0,0,0,0.1)",
              },
              "&:active": {
                cursor: "grabbing",
              },
            }}
          >
            <DragIndicator
              sx={{ fontSize: 16, color: isDarkMode ? "#9ca3af" : "#6b7280" }}
            />
          </Box>
        </Box>
      )}
      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorAnchorEl)}
        anchorEl={colorAnchorEl}
        onClose={handleColorClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Box
          sx={{
            padding: 1.5,
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1,
            backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
          }}
        >
          {COLOR_PALETTE.map((color) => (
            <Box
              key={color}
              onClick={() => handleColorSelect(color)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "4px",
                backgroundColor: color,
                cursor: "pointer",
                border:
                  cardColor === color
                    ? `3px solid ${isDarkMode ? "#ffffff" : "#000000"}`
                    : "2px solid transparent",
                "&:hover": {
                  transform: "scale(1.1)",
                },
                transition: "transform 0.1s ease",
              }}
            />
          ))}
        </Box>
      </Popover>
      {children}
    </Box>
  );
};

// Sortable Section Component
interface SortableSectionProps {
  section: DashboardSection;
  children: React.ReactNode;
  isDraggingEnabled: boolean;
  onDeleteSection: (sectionId: string) => void;
  onEditSectionName: (sectionId: string, newName: string) => void;
  showHeader: boolean; // Whether to show header when not in edit mode
  columnCount: number;
  onColumnChange: (sectionId: string, columns: number) => void;
}

const SortableSection: React.FC<SortableSectionProps> = ({
  section,
  children,
  isDraggingEnabled,
  onDeleteSection,
  onEditSectionName,
  showHeader,
  columnCount,
  onColumnChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id, disabled: !isDraggingEnabled });

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(section.label);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleSaveEdit = () => {
    if (editValue.trim() && editValue !== section.label) {
      onEditSectionName(section.id, editValue.trim());
    }
    setIsEditing(false);
  };

  // Only show header when in edit mode OR when showHeader is true
  const shouldShowHeader = isDraggingEnabled || showHeader;

  return (
    <Box ref={setNodeRef} style={style} sx={{ marginBottom: 3 }}>
      {shouldShowHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            marginBottom: 2,
          }}
        >
          {isDraggingEnabled && (
            <Box
              {...attributes}
              {...listeners}
              sx={{
                cursor: "grab",
                padding: "4px",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                backgroundColor: isDarkMode
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.03)",
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.06)",
                },
                "&:active": {
                  cursor: "grabbing",
                },
              }}
            >
              <DragIndicator
                sx={{ fontSize: 20, color: isDarkMode ? "#9ca3af" : "#6b7280" }}
              />
            </Box>
          )}
          {isEditing ? (
            <TextField
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveEdit();
                if (e.key === "Escape") {
                  setEditValue(section.label);
                  setIsEditing(false);
                }
              }}
              size="small"
              autoFocus
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  padding: "4px 8px",
                },
              }}
            />
          ) : (
            <Typography
              variant="h6"
              onClick={() => isDraggingEnabled && setIsEditing(true)}
              sx={{
                color: isDarkMode ? "#ffffff" : "#111827",
                fontWeight: 600,
                cursor: isDraggingEnabled ? "text" : "default",
                "&:hover": isDraggingEnabled
                  ? {
                      backgroundColor: isDarkMode
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.03)",
                      borderRadius: "4px",
                      padding: "2px 6px",
                      margin: "-2px -6px",
                    }
                  : {},
              }}
            >
              {section.label}
              {section.id === "this-month" &&
                ` (${incomeUtils.getCurrentMonthName()})`}
              {section.id === "next-month" &&
                ` (${incomeUtils.getNextMonthName()})`}
            </Typography>
          )}
          {/* Column Selector - always visible when header is shown */}
          <Box
            sx={{
              marginLeft: isDraggingEnabled ? 0 : "auto",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <ViewColumnIcon
              sx={{
                fontSize: 16,
                color: isDarkMode ? "#6b7280" : "#9ca3af",
              }}
            />
            <Select
              value={columnCount}
              onChange={(e) =>
                onColumnChange(section.id, Number(e.target.value))
              }
              size="small"
              sx={{
                minWidth: 60,
                height: 28,
                "& .MuiSelect-select": {
                  padding: "2px 8px",
                  fontSize: "0.75rem",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDarkMode
                    ? "rgba(255,255,255,0.1)"
                    : "rgba(0,0,0,0.1)",
                },
              }}
            >
              {COLUMN_OPTIONS.map((cols) => (
                <MenuItem key={cols} value={cols} sx={{ fontSize: "0.75rem" }}>
                  {cols} col{cols > 1 ? "s" : ""}
                </MenuItem>
              ))}
            </Select>
          </Box>
          {isDraggingEnabled && (
            <IconButton
              size="small"
              onClick={() => onDeleteSection(section.id)}
              sx={{
                color: isDarkMode ? "#9ca3af" : "#6b7280",
                "&:hover": {
                  color: "#ef4444",
                  backgroundColor: isDarkMode
                    ? "rgba(239,68,68,0.1)"
                    : "rgba(239,68,68,0.1)",
                },
              }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </Box>
      )}
      {children}
    </Box>
  );
};

const DashboardPage: React.FC<DashboardPageProps> = ({ setPageTitle }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const { user } = useAuth();
  const { setShareHandler } = useShareAction();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [income, setIncome] = useState<Income[]>([]);
  const [debts, setDebts] = useState<Debt[]>([]);
  const [bankAccounts, setBankAccounts] = useState<BankAccountBalance[]>([]);
  // Money actually received this month, derived from bank transactions
  // (excludes internal transfers). Powers the "Income › Received" figure.
  const [receivedMonthIncome, setReceivedMonthIncome] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isVisibilityOpen, setIsVisibilityOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelHeight, setPanelHeight] = useState(0);
  const hasMounted = useRef(false);
  const [visibility, setVisibility] = useState<{
    hiddenGroups: Record<string, boolean>;
    hiddenCards: Record<string, boolean>;
  }>({
    hiddenGroups: {},
    hiddenCards: {},
  });

  // Multi-dashboard state
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [activeDashboardId, setActiveDashboardId] = useState<string | null>(
    null
  );
  const [dashboardsLoading, setDashboardsLoading] = useState(true);
  const [createDashboardOpen, setCreateDashboardOpen] = useState(false);
  const [renameDashboard, setRenameDashboard] = useState<Dashboard | null>(
    null
  );
  const [shareDashboard, setShareDashboard] = useState<Dashboard | null>(null);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [incomeProjectionMoM, setIncomeProjectionMoM] = useState<number | null>(
    null
  );
  const [projectedIncomeModalOpen, setProjectedIncomeModalOpen] =
    useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeDashboard = useMemo(
    () => dashboards.find((d) => d.id === activeDashboardId) || null,
    [dashboards, activeDashboardId]
  );

  useEffect(() => {
    setShareHandler(() => {
      if (activeDashboard) {
        setShareDashboard(activeDashboard);
      }
    });
    return () => setShareHandler(null);
  }, [activeDashboard, setShareHandler]);

  // Dynamic sections state (persisted in Supabase dashboards)
  const [sections, setSections] = useState<DashboardSection[]>(() => {
    console.log("[Dashboard] Using default sections layout");
    return DEFAULT_SECTIONS;
  });

  // Loading sections fallback (for skeleton loading state)
  const loadingSections = sections.length > 0 ? sections : DEFAULT_SECTIONS;

  // Drag-and-drop state
  const [isDraggingEnabled, setIsDraggingEnabled] = useState(false);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [activeDragType, setActiveDragType] = useState<
    "section" | "card" | null
  >(null);

  // New section creation state
  const [isAddingSectionOpen, setIsAddingSectionOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");

  // Card colors state (persisted in Supabase dashboards)
  const [cardColors, setCardColors] = useState<Record<string, string>>({});

  // Coloring enabled state (persisted in Supabase dashboards)
  const [coloringEnabled, setColoringEnabled] = useState<boolean>(true);

  // Handler for toggling coloring
  const handleColoringToggle = useCallback((enabled: boolean) => {
    setColoringEnabled(enabled);
    console.log("[Dashboard] Coloring enabled:", enabled);
  }, []);

  // Handler for changing card color
  const handleCardColorChange = useCallback((cardId: string, color: string) => {
    setCardColors((prev) => {
      const newColors = { ...prev, [cardId]: color };
      console.log("[Dashboard] Saved card color:", cardId, color);
      return newColors;
    });
  }, []);

  // Helper to get card color (custom or default, respects coloring toggle)
  const getCardColor = useCallback(
    (cardId: string): string => {
      if (!coloringEnabled) {
        return "#718096"; // Neutral gray when coloring is disabled
      }
      return cardColors[cardId] || DEFAULT_CARD_COLORS[cardId] || "#3182CE";
    },
    [cardColors, coloringEnabled]
  );

  // Section columns state (persisted in Supabase dashboards)
  const [sectionColumns, setSectionColumns] = useState<Record<string, number>>(
    {}
  );

  // Handler for changing section column count
  const handleSectionColumnChange = useCallback(
    (sectionId: string, columns: number) => {
      setSectionColumns((prev) => {
        const newColumns = { ...prev, [sectionId]: columns };
        console.log("[Dashboard] Saved section columns:", sectionId, columns);
        return newColumns;
      });
    },
    []
  );

  // Helper to get section column count (custom or calculated default)
  const getSectionColumnCount = useCallback(
    (sectionId: string, cardCount: number): number => {
      if (sectionColumns[sectionId]) {
        return sectionColumns[sectionId];
      }
      if (sectionId === "annual-overview") return 5;
      // Default: auto-calculate based on card count
      if (cardCount <= 2) return cardCount;
      if (cardCount <= 4) return cardCount;
      if (cardCount <= 6) return 3;
      return 4;
    },
    [sectionColumns]
  );

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Persist layout changes to database with debounce
  useEffect(() => {
    // Debounced save to database
    if (activeDashboardId && !dashboardsLoading) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(async () => {
        try {
          await dashboardAPI.update(activeDashboardId, {
            layout: sections as APIDashboardSection[],
            cardColors,
            sectionColumns,
            coloringEnabled,
            visibility,
          });
          console.log("[Dashboard] Saved to database");
        } catch (err) {
          console.error("[Dashboard] Failed to save to database:", err);
        }
      }, 1000);
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    sections,
    cardColors,
    sectionColumns,
    coloringEnabled,
    visibility,
    activeDashboardId,
    dashboardsLoading,
  ]);

  // Load dashboards from database on mount
  useEffect(() => {
    const loadDashboards = async () => {
      if (!user) {
        setDashboardsLoading(false);
        return;
      }

      try {
        setDashboardsLoading(true);
        const loadedDashboards = await dashboardAPI.getAll();
        console.log("[Dashboard] Loaded dashboards:", loadedDashboards.length);

        if (loadedDashboards.length === 0) {
          // Migrate from localStorage
          console.log(
            "[Dashboard] No dashboards found, migrating from localStorage"
          );
          const storedLayout = localStorage.getItem(LAYOUT_STORAGE_KEY);
          const storedCardColors = localStorage.getItem(
            CARD_COLORS_STORAGE_KEY
          );
          const storedSectionColumns = localStorage.getItem(
            SECTION_COLUMNS_STORAGE_KEY
          );
          const storedColoringEnabled = localStorage.getItem(
            COLORING_ENABLED_STORAGE_KEY
          );
          const storedVisibility = localStorage.getItem(
            "dashboard-card-visibility"
          );

          let layoutToMigrate = DEFAULT_SECTIONS;
          if (storedLayout) {
            try {
              layoutToMigrate = insertMissingProjectedIncomeCard(
                removeCashflowSection(
                  insertMissingNextDaySections(JSON.parse(storedLayout))
                )
              );
            } catch (e) {
              console.warn(
                "[Dashboard] Failed to parse stored layout for migration"
              );
            }
          }

          const newDashboard = await dashboardAPI.create({
            name: "My Dashboard",
            description: "",
            isDefault: true,
            layout: layoutToMigrate as APIDashboardSection[],
            cardColors: storedCardColors ? JSON.parse(storedCardColors) : {},
            sectionColumns: storedSectionColumns
              ? JSON.parse(storedSectionColumns)
              : {},
            coloringEnabled: storedColoringEnabled !== "false",
            visibility: storedVisibility
              ? JSON.parse(storedVisibility)
              : { hiddenGroups: {}, hiddenCards: {} },
          });

          console.log(
            "[Dashboard] Created default dashboard:",
            newDashboard.id
          );
          setDashboards([newDashboard]);
          setActiveDashboardId(newDashboard.id);
          localStorage.removeItem(LAYOUT_STORAGE_KEY);
          localStorage.removeItem(CARD_COLORS_STORAGE_KEY);
          localStorage.removeItem(SECTION_COLUMNS_STORAGE_KEY);
          localStorage.removeItem(COLORING_ENABLED_STORAGE_KEY);
          localStorage.removeItem("dashboard-card-visibility");
          console.log("[Dashboard] Cleared localStorage dashboard layout keys");
        } else {
          setDashboards(loadedDashboards);
          // Set active to default or first dashboard
          const defaultDashboard = loadedDashboards.find((d) => d.isDefault);
          setActiveDashboardId(defaultDashboard?.id || loadedDashboards[0].id);
        }
      } catch (err) {
        if (!isAuthTransitionError(err)) {
          console.error("[Dashboard] Failed to load dashboards:", err);
        }
        // Fallback to localStorage
        setDashboards([]);
        setActiveDashboardId(null);
      } finally {
        setDashboardsLoading(false);
      }
    };

    loadDashboards();
  }, [user]);

  // Update local state when active dashboard changes
  useEffect(() => {
    if (activeDashboard) {
      console.log("[Dashboard] Switching to dashboard:", activeDashboard.name);
      setSections(
        insertMissingProjectedIncomeCard(
          removeCashflowSection(
            insertMissingNextDaySections(
              activeDashboard.layout as DashboardSection[]
            )
          )
        )
      );
      setCardColors(activeDashboard.cardColors || {});
      setSectionColumns(activeDashboard.sectionColumns || {});
      setColoringEnabled(activeDashboard.coloringEnabled !== false);
      setVisibility(
        activeDashboard.visibility || { hiddenGroups: {}, hiddenCards: {} }
      );
    }
  }, [activeDashboard]);

  // Dashboard tab handlers
  const handleDashboardTabChange = useCallback((dashboardId: string) => {
    setActiveDashboardId(dashboardId);
  }, []);

  const handleCreateDashboard = useCallback(
    async (data: {
      name: string;
      description: string;
      template: DashboardTemplate;
    }) => {
      setDashboardError(null);
      try {
        let layoutToUse = [] as DashboardSection[];
        let colorsToUse = {};
        let columnsToUse = {};
        let coloringToUse = true;
        let visibilityToUse = { hiddenGroups: {}, hiddenCards: {} };

        if (data.template === "copy-current" && activeDashboard) {
          layoutToUse = activeDashboard.layout as DashboardSection[];
          colorsToUse = activeDashboard.cardColors || {};
          columnsToUse = activeDashboard.sectionColumns || {};
          coloringToUse = activeDashboard.coloringEnabled !== false;
          visibilityToUse = activeDashboard.visibility || visibilityToUse;
        } else if (data.template === "default") {
          layoutToUse = DEFAULT_SECTIONS;
          colorsToUse = DEFAULT_CARD_COLORS;
        }

        const newDashboard = await dashboardAPI.create({
          name: data.name,
          description: data.description,
          isDefault: false,
          layout: layoutToUse as APIDashboardSection[],
          cardColors: colorsToUse,
          sectionColumns: columnsToUse,
          coloringEnabled: coloringToUse,
          visibility: visibilityToUse,
        });

        setDashboards((prev) => [...prev, newDashboard]);
        setActiveDashboardId(newDashboard.id);
        setCreateDashboardOpen(false);
      } catch (err) {
        console.error("[Dashboard] Failed to create dashboard:", err);
        setDashboardError(
          err instanceof Error ? err.message : "Failed to create dashboard"
        );
      }
    },
    [activeDashboard]
  );

  const handleRenameDashboard = useCallback(
    async (data: { name: string; description: string }) => {
      if (!renameDashboard) return;
      setDashboardError(null);
      try {
        const updated = await dashboardAPI.update(renameDashboard.id, {
          name: data.name,
          description: data.description,
        });
        setDashboards((prev) =>
          prev.map((d) => (d.id === updated.id ? updated : d))
        );
        setRenameDashboard(null);
      } catch (err) {
        console.error("[Dashboard] Failed to rename dashboard:", err);
        setDashboardError(
          err instanceof Error ? err.message : "Failed to rename dashboard"
        );
      }
    },
    [renameDashboard]
  );

  const handleSetDefaultDashboard = useCallback(async (dashboardId: string) => {
    try {
      await dashboardAPI.setDefault(dashboardId);
      setDashboards((prev) =>
        prev.map((d) => ({
          ...d,
          isDefault: d.id === dashboardId,
        }))
      );
    } catch (err) {
      console.error("[Dashboard] Failed to set default dashboard:", err);
    }
  }, []);

  const handleDeleteDashboard = useCallback(
    async (dashboardId: string) => {
      if (dashboards.length <= 1) {
        console.warn("[Dashboard] Cannot delete the only dashboard");
        return;
      }

      if (!window.confirm("Are you sure you want to delete this dashboard?")) {
        return;
      }

      try {
        await dashboardAPI.delete(dashboardId);
        setDashboards((prev) => prev.filter((d) => d.id !== dashboardId));
        if (activeDashboardId === dashboardId) {
          const remaining = dashboards.filter((d) => d.id !== dashboardId);
          const newActive = remaining.find((d) => d.isDefault) || remaining[0];
          setActiveDashboardId(newActive?.id || null);
        }
      } catch (err) {
        console.error("[Dashboard] Failed to delete dashboard:", err);
      }
    },
    [dashboards, activeDashboardId]
  );

  // Capture snapshot data for sharing
  const captureSnapshotData = useCallback(async () => {
    return {
      debts,
      expenses,
      income,
      goals: [], // Add goals if available
      bankAccounts: bankAccounts.map((a) => ({
        name: a.name,
        officialName: a.officialName,
        type: a.type,
        balance: a.balance,
        available: a.available,
        lastSynced: a.lastSynced,
        institution: a.institution,
        mask: a.mask,
      })),
    };
  }, [debts, expenses, income, bankAccounts]);

  // Debt quality filter state - defaults to "bad" (show bad debt only)
  type DebtFilterType = "all" | "good" | "bad";
  const [debtFilter] = useState<DebtFilterType>(() => {
    const stored = localStorage.getItem("dashboard-debt-quality-filter");
    return (stored as DebtFilterType) || "bad";
  });

  // State for breakdown modal
  const [openBreakdownModal, setOpenBreakdownModal] = useState<{
    items: BreakdownItem[];
    title: string;
  } | null>(null);

  // State for income history date range (defaults to last 90 days)
  const [incomeHistoryDays, setIncomeHistoryDays] = useState<number>(() => {
    const stored = localStorage.getItem("dashboard-income-history-days");
    return stored ? parseInt(stored, 10) : 90;
  });

  // Persist income history days setting
  useEffect(() => {
    localStorage.setItem(
      "dashboard-income-history-days",
      incomeHistoryDays.toString()
    );
  }, [incomeHistoryDays]);

  // Set page title
  useEffect(() => {
    if (setPageTitle) {
      setPageTitle("Dashboard");
    }
  }, [setPageTitle]);

  // Persist good debt filter setting
  // Persist debt quality filter setting
  useEffect(() => {
    localStorage.setItem("dashboard-debt-quality-filter", debtFilter);
  }, [debtFilter]);

  // Load expenses, income, and debts from database on initial render
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        // Load expenses, income, manual debts, Plaid credit cards, and bank accounts using APIs
        const [
          allExpenses,
          allIncome,
          allDebts,
          plaidCreditCards,
          allBankAccounts,
        ] = await Promise.all([
          expenseAPI.getAll(),
          incomeAPI.getAll(),
          debtDB.getAllDebts(),
          plaidCreditCardService.getCreditCardAccounts(),
          plaidBankAccountService.getBankAccounts(),
        ]);

        // Ensure all dueDates are Date objects and amounts are numbers for expenses
        const formattedExpenses = allExpenses.map((expense) => {
          const formatted = { ...expense };

          // Convert dueDate string to Date object preserving UTC
          if (typeof expense.dueDate === "string") {
            // Parse the UTC string directly to preserve the date
            formatted.dueDate = new Date(expense.dueDate);
          }

          // Ensure amount is a number
          if (typeof expense.amount === "string") {
            formatted.amount = parseFloat(expense.amount) || 0;
          }

          return formatted as Expense;
        });

        // Ensure all dueDates are Date objects and amounts are numbers for income
        const formattedIncome = allIncome.map((inc) => {
          const formatted = { ...inc };

          // Convert expectedByDate string to Date object preserving UTC
          if (typeof inc.expectedByDate === "string") {
            // Parse the UTC string directly to preserve the date
            formatted.expectedByDate = new Date(inc.expectedByDate);
          }

          // Ensure invoiceTotal and paidToDate are numbers
          if (typeof inc.invoiceTotal === "string") {
            formatted.invoiceTotal = parseFloat(inc.invoiceTotal) || 0;
          }
          if (typeof inc.paidToDate === "string") {
            formatted.paidToDate = parseFloat(inc.paidToDate) || 0;
          }

          return formatted as Income;
        });

        // Merge manual debts with Plaid credit cards
        const allDebtsWithPlaid = [...allDebts, ...plaidCreditCards];

        // Set the formatted data
        setExpenses(formattedExpenses);
        setIncome(formattedIncome);
        setDebts(allDebtsWithPlaid);
        setBankAccounts(allBankAccounts);
      } catch (error) {
        if (isAbortError(error)) {
          console.debug("[DEBUG] Dashboard loadData aborted", error);
        } else {
          console.error("Error loading data:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Load this month's bank transactions to compute income actually *received*
  // (money in, excluding internal transfers). Kept separate from loadData so a
  // transactions failure never blocks the rest of the dashboard.
  useEffect(() => {
    const loadReceived = async () => {
      try {
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const toISODate = (d: Date) => d.toISOString().slice(0, 10);
        const transactions = await plaidService.getTransactions(
          toISODate(monthStart),
          toISODate(now)
        );
        const received = getCurrentMonthReceivedIncomeTotal(transactions);
        console.debug("[Dashboard] Received income (bank, this month)", {
          transactionCount: transactions.length,
          received,
        });
        setReceivedMonthIncome(received);
      } catch (error) {
        if (isAbortError(error)) {
          console.debug(
            "[DEBUG] Dashboard received-income load aborted",
            error
          );
        } else {
          // Non-fatal: leave received at 0 if transactions are unavailable.
          console.warn(
            "Unable to load received income from transactions",
            error
          );
        }
      }
    };

    loadReceived();
  }, []);

  // --- Focus: Finance-native income management from breakdown modals -------------
  // Rows imported from TPC Account Center are managed upstream, so these only
  // ever run for records that originate in Focus: Finance.
  const [incomeToEdit, setIncomeToEdit] = useState<Income | null>(null);
  const [incomeToDelete, setIncomeToDelete] = useState<Income[]>([]);
  const [incomeToSuspend, setIncomeToSuspend] = useState<Income[]>([]);
  const [suspendUntil, setSuspendUntil] = useState<string>("");
  const [incomeActionError, setIncomeActionError] = useState<string | null>(
    null
  );

  const refreshIncome = useCallback(async () => {
    const allIncome = await incomeAPI.getAll();
    setIncome(
      allIncome.map((inc) => {
        const formatted = { ...inc };
        if (typeof inc.expectedByDate === "string") {
          formatted.expectedByDate = new Date(inc.expectedByDate);
        }
        if (typeof inc.invoiceTotal === "string") {
          formatted.invoiceTotal = parseFloat(inc.invoiceTotal) || 0;
        }
        if (typeof inc.paidToDate === "string") {
          formatted.paidToDate = parseFloat(inc.paidToDate) || 0;
        }
        return formatted as Income;
      })
    );
  }, []);

  const findIncomeForItems = useCallback(
    (items: BreakdownItem[]): Income[] => {
      const resolved = new Map<string, Income>();
      items
        .filter((item) => item.entityType === "income" && item.entityId)
        .forEach((item) => {
          const match = income.find((inc) => inc.id === item.entityId);
          if (!match) return;
          // Recurring occurrences are generated client-side of the database:
          // their ids look like "<realId>-recurring-3" and no such row exists,
          // so writing to them 500s. Act on the series record instead.
          const baseId = match.originalId || match.id;
          const base = income.find((inc) => inc.id === baseId) || match;
          resolved.set(base.id, base);
        });
      return Array.from(resolved.values());
    },
    [income]
  );

  const incomeBreakdownActions: BreakdownItemActions = useMemo(
    () => ({
      onEdit: (item) => {
        const [target] = findIncomeForItems([item]);
        if (target) setIncomeToEdit(target);
      },
      onDelete: (items) => {
        const targets = findIncomeForItems(items);
        if (targets.length) {
          setIncomeActionError(null);
          setIncomeToDelete(targets);
        }
      },
      onSuspend: (items) => {
        const targets = findIncomeForItems(items);
        if (!targets.length) return;
        // Prefill from an existing end date, otherwise default to one month
        // out. Still editable, and clearing the field suspends indefinitely.
        const existing = targets.find((t) => t.suspendedUntil);
        setSuspendUntil(
          existing?.suspendedUntil
            ? new Date(existing.suspendedUntil).toISOString().slice(0, 10)
            : defaultSuspendUntil()
        );
        setIncomeActionError(null);
        setIncomeToSuspend(targets);
      },
    }),
    [findIncomeForItems]
  );

  const handleUpdateIncome = useCallback(
    async (updated: Income) => {
      console.debug("[Dashboard] Updating income", updated.id);
      await incomeDB.updateIncome(updated);
      await refreshIncome();
      setIncomeToEdit(null);
    },
    [refreshIncome]
  );

  const handleConfirmDeleteIncome = useCallback(async () => {
    if (!incomeToDelete.length) return;
    console.debug(
      "[Dashboard] Deleting income",
      incomeToDelete.map((inc) => inc.id)
    );
    try {
      await Promise.all(
        incomeToDelete.map((inc) => incomeDB.deleteIncome(inc.id))
      );
      await refreshIncome();
      setIncomeToDelete([]);
    } catch (error) {
      console.error("[Dashboard] Delete income failed", error);
      setIncomeActionError(
        error instanceof Error ? error.message : "Failed to delete income."
      );
    }
  }, [incomeToDelete, refreshIncome]);

  const handleApplySuspension = useCallback(
    async (mode: "indefinite" | "until" | "resume") => {
      if (!incomeToSuspend.length) return;
      let suspendedUntil: string | null = null;
      if (mode === "until") {
        const parsed = new Date(`${suspendUntil}T00:00:00.000Z`);
        if (!suspendUntil || isNaN(parsed.getTime())) {
          setIncomeActionError("Enter a valid date, or suspend indefinitely.");
          return;
        }
        suspendedUntil = parsed.toISOString();
      }
      const shouldSuspend = mode !== "resume";
      console.debug("[Dashboard] Suspension change", {
        ids: incomeToSuspend.map((inc) => inc.id),
        mode,
        suspendedUntil,
      });
      try {
        const saved = await Promise.all(
          incomeToSuspend.map((inc) =>
            incomeDB.updateIncome({
              ...inc,
              isSuspended: shouldSuspend,
              suspendedUntil,
            })
          )
        );
        // The API tolerates unknown columns by dropping them, so a 200 alone
        // does not prove the flag stuck. Confirm it round-tripped.
        const notApplied = saved.filter(
          (rec) => Boolean(rec?.isSuspended) !== shouldSuspend
        );
        if (notApplied.length > 0) {
          throw new Error(
            "The server did not save the suspension. The income table may be " +
              "missing its suspension columns."
          );
        }
        await refreshIncome();
        setIncomeToSuspend([]);
      } catch (error) {
        console.error("[Dashboard] Suspension change failed", error);
        setIncomeActionError(
          error instanceof Error
            ? error.message
            : "Failed to change suspension."
        );
      }
    },
    [incomeToSuspend, suspendUntil, refreshIncome]
  );

  // Handle sync for bank accounts
  const handleSyncBankAccounts = useCallback(async (): Promise<{
    success: boolean;
    message: string;
  }> => {
    const result = await plaidBankAccountService.syncAccounts();

    if (result.success) {
      // Refresh bank accounts from cache (which was just updated)
      const updatedAccounts = await plaidBankAccountService.getBankAccounts();
      setBankAccounts(updatedAccounts);
    }

    return result;
  }, []);

  // Filter debts based on debt quality filter
  // Note: Handle both camelCase (debtQuality) and snake_case (debt_quality) from API
  const filteredDebts = useMemo(() => {
    if (debtFilter === "all") return debts;
    return debts.filter((debt) => {
      const quality =
        debt.debtQuality ??
        (debt as unknown as { debt_quality?: string }).debt_quality;
      return quality === debtFilter;
    });
  }, [debts, debtFilter]);

  // Calculate current month totals for expenses
  const currentYear = new Date().getFullYear();
  const pastYear = currentYear - 1;
  const remainingMonthExpenses = expenseUtils.getRemainingMonthExpensesTotal(
    expenses,
    debts
  );
  const totalMonthExpenses = expenseUtils.getCurrentMonthExpensesTotal(
    expenses,
    debts
  );

  // Calculate current month totals for income
  const remainingMonthIncome = incomeUtils.getRemainingMonthIncomeTotal(income);
  // Income minus anything currently suspended. Dashboard totals and their
  // breakdown modals both read from this so the rows always sum to the card.
  const activeIncome = useMemo(
    () => incomeUtils.getActiveIncome(income),
    [income]
  );
  const totalMonthIncome = incomeUtils.getCurrentMonthIncomeTotal(income);
  // Gross income expected this month (invoiced/projected), independent of how
  // much has been collected — the "Income › Expected" figure.
  const expectedMonthIncome =
    incomeUtils.getCurrentMonthExpectedIncomeTotal(income);

  // Calculate bank account balances
  const checkingAccounts = bankAccounts.filter((a) => a.type === "checking");
  const savingsAccounts = bankAccounts.filter((a) => a.type === "savings");
  const totalCheckingBalance = checkingAccounts.reduce(
    (sum, a) => sum + a.balance,
    0
  );
  const totalSavingsBalance = savingsAccounts.reduce(
    (sum, a) => sum + a.balance,
    0
  );

  // Calculate income history for configurable date range
  const incomeHistoryData = useMemo(() => {
    const now = new Date();
    const startDate = new Date(now);
    startDate.setDate(startDate.getDate() - incomeHistoryDays);

    const filteredIncome = income.filter((inc) => {
      const incomeDate = new Date(inc.expectedByDate);
      return incomeDate >= startDate && incomeDate <= now && inc.isPaid;
    });

    const total = filteredIncome.reduce(
      (sum, inc) => sum + Number(inc.invoiceTotal || 0),
      0
    );

    const items: BreakdownItem[] = filteredIncome.map((inc) => ({
      name: inc.client,
      sourceProvider: inc.sourceProvider,
      sourceDocumentId: inc.sourceDocumentId,
      sourceDocumentType: inc.sourceDocumentType,
      sourceClientId: inc.sourceClientId,
      amount: Number(inc.invoiceTotal || 0),
      date: inc.expectedByDate,
    }));

    return { total, items, count: filteredIncome.length };
  }, [income, incomeHistoryDays]);

  // Calculate cash flow
  const cashFlow = totalMonthIncome - totalMonthExpenses;

  // Calculate actual cash flow (only received income - paid expenses this month)
  const actualReceivedIncome = incomeUtils
    .getCurrentMonthIncome(income)
    .filter((inc) => Boolean(inc.receivedDate))
    .reduce((sum, inc) => sum + incomeUtils.getInvoiceTotal(inc), 0);
  const actualPaidExpenses = expenseUtils
    .getCurrentMonthExpenses(expenses)
    .filter((exp) => exp.isPaid && !exp.isSkipped)
    .reduce((sum, exp) => sum + Number(exp.amount), 0);
  const actualCashFlow = actualReceivedIncome - actualPaidExpenses;

  // Projected cash flow (all income - all expenses for the period)
  const projectedCashFlow = cashFlow;

  console.debug("[Dashboard] Cashflow - Actual", {
    actualReceivedIncome,
    actualPaidExpenses,
    actualCashFlow,
  });
  console.debug("[Dashboard] Cashflow - Projected", {
    totalMonthIncome,
    totalMonthExpenses,
    projectedCashFlow,
  });

  const projectionYear = 2026;
  const projectionToday = useMemo(() => new Date(), []);
  const projectionYearStart = useMemo(
    () => new Date(Date.UTC(projectionYear, 0, 1)),
    [projectionYear]
  );
  const projectionYearEnd = useMemo(
    () => new Date(Date.UTC(projectionYear + 1, 0, 1)),
    [projectionYear]
  );
  const projectionDaysInYear = useMemo(
    () =>
      Math.floor(
        (projectionYearEnd.getTime() - projectionYearStart.getTime()) / 86400000
      ),
    [projectionYearEnd, projectionYearStart]
  );
  const projectionTodayUtc = useMemo(
    () =>
      new Date(
        Date.UTC(
          projectionToday.getUTCFullYear(),
          projectionToday.getUTCMonth(),
          projectionToday.getUTCDate()
        )
      ),
    [projectionToday]
  );
  const projectionEndDate = useMemo(
    () =>
      projectionToday.getUTCFullYear() === projectionYear
        ? new Date(
            Date.UTC(
              projectionToday.getUTCFullYear(),
              projectionToday.getUTCMonth(),
              projectionToday.getUTCDate(),
              23,
              59,
              59,
              999
            )
          )
        : new Date(Date.UTC(projectionYear, 11, 31, 23, 59, 59, 999)),
    [projectionToday, projectionYear]
  );
  const projectionDayOfYear = useMemo(
    () =>
      projectionToday.getUTCFullYear() === projectionYear
        ? Math.max(
            1,
            Math.floor(
              (projectionTodayUtc.getTime() - projectionYearStart.getTime()) /
                86400000
            ) + 1
          )
        : projectionDaysInYear,
    [
      projectionDaysInYear,
      projectionToday,
      projectionTodayUtc,
      projectionYear,
      projectionYearStart,
    ]
  );
  const projectionYtdIncomeTotal = income.reduce((sum, inc) => {
    const expectedDate = inc.expectedByDate
      ? new Date(inc.expectedByDate)
      : null;
    if (!expectedDate || Number.isNaN(expectedDate.getTime())) return sum;
    if (
      expectedDate >= projectionYearStart &&
      expectedDate <= projectionEndDate
    ) {
      return sum + incomeUtils.getInvoiceTotal(inc);
    }
    return sum;
  }, 0);
  const projectionAvgDailyIncome =
    projectionDayOfYear > 0
      ? projectionYtdIncomeTotal / projectionDayOfYear
      : 0;
  const projectedIncome2026 = projectionAvgDailyIncome * projectionDaysInYear;
  const projectionLogDate = useMemo(
    () => projectionTodayUtc.toISOString().slice(0, 10),
    [projectionTodayUtc]
  );
  const projectionIncomeItems = useMemo(() => {
    return income.filter((inc) => {
      const expectedDate = inc.expectedByDate
        ? new Date(inc.expectedByDate)
        : null;
      if (!expectedDate || Number.isNaN(expectedDate.getTime())) return false;
      return (
        expectedDate >= projectionYearStart && expectedDate <= projectionEndDate
      );
    });
  }, [income, projectionYearStart, projectionEndDate]);

  const projectionIncomeByPayee = useMemo(() => {
    const groups = new Map<string, { total: number; items: Income[] }>();
    projectionIncomeItems.forEach((inc) => {
      const payee = inc.client || "Unknown";
      const total = groups.get(payee)?.total ?? 0;
      const items = groups.get(payee)?.items ?? [];
      groups.set(payee, {
        total: total + incomeUtils.getInvoiceTotal(inc),
        items: [...items, inc],
      });
    });
    return Array.from(groups.entries())
      .map(([payee, data]) => ({ payee, ...data }))
      .sort((a, b) => b.total - a.total);
  }, [projectionIncomeItems]);

  console.debug("[Dashboard] Income projection 2026", {
    projectionYear,
    projectionDayOfYear,
    projectionDaysInYear,
    projectionYtdIncomeTotal,
    projectionAvgDailyIncome,
    projectedIncome2026,
  });

  useEffect(() => {
    if (!projectedIncomeModalOpen) return;
    console.debug("[Dashboard] Open projected income modal", {
      items: projectionIncomeItems.length,
      payees: projectionIncomeByPayee.length,
    });
  }, [
    projectedIncomeModalOpen,
    projectionIncomeItems,
    projectionIncomeByPayee,
  ]);

  useEffect(() => {
    if (projectionToday.getUTCFullYear() !== projectionYear) return;
    void upsertDailyAverage(projectionLogDate, projectionAvgDailyIncome);
  }, [
    projectionAvgDailyIncome,
    projectionLogDate,
    projectionYear,
    projectionToday,
  ]);

  useEffect(() => {
    if (projectionToday.getUTCFullYear() !== projectionYear) return;
    const toIsoDate = (date: Date) => date.toISOString().slice(0, 10);
    const currentMonth = projectionToday.getUTCMonth();
    const currentMonthEnd = new Date(
      Date.UTC(projectionYear, currentMonth + 1, 0)
    );
    const previousMonthStart = new Date(
      Date.UTC(projectionYear, currentMonth - 1, 1)
    );

    const startIso = toIsoDate(previousMonthStart);
    const endIso = toIsoDate(currentMonthEnd);

    fetchDailyAverages(startIso, endIso)
      .then((dailyLogs) => {
        const averageForMonth = (year: number, monthIndex: number) => {
          const monthLogs = dailyLogs.filter((log) => {
            const logDate = new Date(log.log_date);
            return (
              logDate.getUTCFullYear() === year &&
              logDate.getUTCMonth() === monthIndex
            );
          });
          if (monthLogs.length === 0) return null;
          const total = monthLogs.reduce(
            (sum, log) => sum + Number(log.avg_daily_income || 0),
            0
          );
          return total / monthLogs.length;
        };

        const currentAvg = averageForMonth(projectionYear, currentMonth);
        const previousAvg = averageForMonth(projectionYear, currentMonth - 1);
        if (currentAvg === null || previousAvg === null || previousAvg === 0) {
          setIncomeProjectionMoM(null);
        } else {
          setIncomeProjectionMoM(
            ((currentAvg - previousAvg) / previousAvg) * 100
          );
        }
        console.debug("[Dashboard] Income projection MoM", {
          currentMonth,
          currentAvg,
          previousAvg,
        });
      })
      .catch((error) => {
        if (!isAuthTransitionError(error)) {
          console.error(
            "[Dashboard] Failed to load income projection logs",
            error
          );
        }
      });
  }, [projectionLogDate, projectionYear, projectionToday]);

  const totalMonthlyDebtPayments = filteredDebts.reduce(
    (sum, debt) => sum + Number(debt.maxMonthlyPayment || 0),
    0
  );

  // Calculate credit utilization (using filtered debts)
  const totalCreditLimit = filteredDebts
    .filter((debt) => debt.type === "Credit Card")
    .reduce((sum, debt) => sum + Number(debt.creditLimit || 0), 0);
  const totalCreditBalance = filteredDebts
    .filter((debt) => debt.type === "Credit Card")
    .reduce((sum, debt) => sum + Number(debt.balance || 0), 0);
  const creditUtilization =
    totalCreditLimit > 0
      ? Math.round((totalCreditBalance / totalCreditLimit) * 100)
      : 0;

  // Calculate total paydown amount from the Paydown column (using filtered debts)
  const totalPaydownAmount = filteredDebts.reduce(
    (sum, debt) => sum + Math.abs(Number(debt.paydownAmount || 0)),
    0
  );

  // Calculate personal debts total (Personal Loans and Other only)
  const personalDebts = filteredDebts.filter((debt) => {
    const type = debt.type?.toLowerCase() || "";
    return type === "personal loan" || type === "other";
  });
  const totalPersonalDebtBalance = personalDebts.reduce(
    (sum, debt) => sum + Number(debt.balance || 0),
    0
  );
  const topPersonalDebtGroups = groupDebtsByCreditor(personalDebts).slice(0, 5);
  console.debug(
    "[Dashboard] Top personal creditor groups:",
    topPersonalDebtGroups.map((group) => ({
      creditor: group.creditor,
      totalBalance: group.totalBalance,
      debtCount: group.debts.length,
    }))
  );

  // Calculate business debts total
  const businessDebts = filteredDebts.filter((debt) =>
    debt.type?.toLowerCase().includes("business")
  );
  const totalBusinessDebtBalance = businessDebts.reduce(
    (sum, debt) => sum + Number(debt.balance || 0),
    0
  );
  const topBusinessDebts = [...businessDebts]
    .sort(
      (a, b) =>
        Math.abs(Number(b.balance || 0)) - Math.abs(Number(a.balance || 0))
    )
    .slice(0, 3);

  // Calculate credit cards total and top balances
  const creditCardDebts = filteredDebts.filter(
    (debt) => debt.type === "Credit Card"
  );
  const topCreditCards = [...creditCardDebts]
    .sort(
      (a, b) =>
        Math.abs(Number(b.balance || 0)) - Math.abs(Number(a.balance || 0))
    )
    .slice(0, 3);

  // Get upcoming expenses (within 30 days)
  const today = new Date();
  const thirtyDaysFromNow = new Date(today);
  thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
  const tenDaysFromNow = new Date(today);
  tenDaysFromNow.setDate(tenDaysFromNow.getDate() + 10);
  const sevenDaysFromNow = new Date(today);
  sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);

  const upcomingExpenses = expenses.filter((expense) => {
    const dueDate = new Date(expense.dueDate);
    return dueDate >= today && dueDate <= thirtyDaysFromNow && !expense.isPaid;
  });
  const nextTenDayExpenses = expenses.filter((expense) => {
    const dueDate = new Date(expense.dueDate);
    return dueDate >= today && dueDate <= tenDaysFromNow && !expense.isPaid;
  });
  const nextSevenDayExpenses = expenses.filter((expense) => {
    const dueDate = new Date(expense.dueDate);
    return dueDate >= today && dueDate <= sevenDaysFromNow && !expense.isPaid;
  });

  // Get upcoming income (within 30 days)
  // Suspended income is excluded everywhere it would be counted or listed;
  // it is resumed from the Suspended section on the Income page.
  const upcomingIncome = activeIncome.filter((inc) => {
    const dueDate = new Date(inc.expectedByDate);
    return dueDate >= today && dueDate <= thirtyDaysFromNow && !inc.isPaid;
  });
  const nextTenDayIncome = activeIncome.filter((inc) => {
    const dueDate = new Date(inc.expectedByDate);
    return dueDate >= today && dueDate <= tenDaysFromNow && !inc.isPaid;
  });
  const nextSevenDayIncome = activeIncome.filter((inc) => {
    const dueDate = new Date(inc.expectedByDate);
    return dueDate >= today && dueDate <= sevenDaysFromNow && !inc.isPaid;
  });

  // Get upcoming debt payments (within 30 days, using filtered debts)
  const upcomingDebtPayments = filteredDebts.filter((debt) => {
    const nextDue = debt.dueDate ? new Date(debt.dueDate) : null;
    return (
      nextDue &&
      nextDue >= today &&
      nextDue <= thirtyDaysFromNow &&
      Number(debt.balance || 0) > 0
    );
  });
  const nextTenDayDebtPayments = filteredDebts.filter((debt) => {
    const nextDue = debt.dueDate ? new Date(debt.dueDate) : null;
    return (
      nextDue &&
      nextDue >= today &&
      nextDue <= tenDaysFromNow &&
      Number(debt.balance || 0) > 0
    );
  });
  const nextSevenDayDebtPayments = filteredDebts.filter((debt) => {
    const nextDue = debt.dueDate ? new Date(debt.dueDate) : null;
    return (
      nextDue &&
      nextDue >= today &&
      nextDue <= sevenDaysFromNow &&
      Number(debt.balance || 0) > 0
    );
  });
  console.debug("[Dashboard] Next day ranges", {
    nextTenExpenses: nextTenDayExpenses.length,
    nextSevenExpenses: nextSevenDayExpenses.length,
    nextTenIncome: nextTenDayIncome.length,
    nextSevenIncome: nextSevenDayIncome.length,
    nextTenDebtPayments: nextTenDayDebtPayments.length,
    nextSevenDebtPayments: nextSevenDayDebtPayments.length,
  });

  // Get next month date range (UTC to match server-generated recurring dates)
  const nextMonthStart = new Date(
    Date.UTC(today.getFullYear(), today.getMonth() + 1, 1)
  );
  const nextMonthEnd = new Date(
    Date.UTC(today.getFullYear(), today.getMonth() + 2, 0, 23, 59, 59, 999)
  );
  const nextMonthName = nextMonthStart.toLocaleString("default", {
    month: "long",
    timeZone: "UTC",
  });

  // Get next month expenses
  const nextMonthExpenses = expenses.filter((expense) => {
    const dueDate = new Date(expense.dueDate);
    return (
      dueDate >= nextMonthStart && dueDate <= nextMonthEnd && !expense.isPaid
    );
  });

  // Get next month income — server already expands recurring income into future instances,
  // so we just filter the income array for next month's date range
  const nextMonthIncome = activeIncome.filter((inc) => {
    const dueDate = new Date(inc.expectedByDate);
    return dueDate >= nextMonthStart && dueDate <= nextMonthEnd && !inc.isPaid;
  });
  console.log("[Dashboard] Next month income:", {
    total: nextMonthIncome.length,
    items: nextMonthIncome.map((inc) => ({
      client: inc.client,
      date: inc.expectedByDate,
    })),
  });

  // Get next month debt payments (using filtered debts)
  const nextMonthDebtPayments = filteredDebts.filter((debt) => {
    const nextDue = debt.dueDate ? new Date(debt.dueDate) : null;
    return (
      nextDue &&
      nextDue >= nextMonthStart &&
      nextDue <= nextMonthEnd &&
      Number(debt.balance || 0) > 0
    );
  });

  // Compute breakdown items for each card
  const breakdowns = useMemo(() => {
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const currentYearStart = new Date(currentYear, 0, 1);
    const currentYearEnd = new Date(currentYear, 11, 31, 23, 59, 59, 999);
    const pastYearStart = new Date(pastYear, 0, 1);
    const pastYearEnd = new Date(pastYear, 11, 31, 23, 59, 59, 999);

    // Remaining expenses breakdown (unpaid expenses this month)
    const remainingExpensesItems: BreakdownItem[] = expenses
      .filter((exp) => {
        const dueDate = new Date(exp.dueDate);
        return (
          !exp.isPaid &&
          dueDate >= currentMonthStart &&
          dueDate <= currentMonthEnd
        );
      })
      .map((exp) => ({
        name: exp.name,
        amount: -Math.abs(Number(exp.amount)),
        date: exp.dueDate,
      }));

    // Remaining income breakdown (unpaid income this month) — uses same util as card value
    // Income whose balance is settled contributes nothing, so listing it is
    // noise — the record is still on the Income page. Applies only to the
    // breakdowns that count balances, not the gross annual views.
    const withBalance = (items: BreakdownItem[]) =>
      items.filter((item) => item.amount !== 0);

    const remainingIncomeItems: BreakdownItem[] = incomeUtils
      .getRemainingMonthIncome(activeIncome)
      .map((inc) => ({
        name: inc.client,
        entityId: inc.id,
        entityType: "income" as const,
        isSuspended: incomeUtils.isIncomeSuspended(inc),
        suspendedUntil: inc.suspendedUntil,
        sourceProvider: inc.sourceProvider,
        sourceDocumentId: inc.sourceDocumentId,
        sourceDocumentType: inc.sourceDocumentType,
        sourceClientId: inc.sourceClientId,
        amount: incomeUtils.calculateOwed(inc),
        date: inc.expectedByDate,
      }));

    // Monthly debt payments breakdown (uses filtered debts)
    const monthlyDebtPaymentsItems: BreakdownItem[] = filteredDebts
      .filter((debt) => Number(debt.maxMonthlyPayment || 0) > 0)
      .map((debt) => ({
        name:
          debt.creditor && debt.account
            ? `${debt.creditor} - ${debt.account}`
            : debt.creditor || debt.account || "Unknown",
        amount: Number(debt.maxMonthlyPayment || 0),
        type: debt.type,
      }));

    // Total expenses breakdown (all expenses this month)
    const totalExpensesItems: BreakdownItem[] = expenses
      .filter((exp) => {
        const dueDate = new Date(exp.dueDate);
        return dueDate >= currentMonthStart && dueDate <= currentMonthEnd;
      })
      .map((exp) => ({
        name: exp.name,
        amount: -Math.abs(Number(exp.amount)),
        date: exp.dueDate,
      }));

    // Total income breakdown (all income this month) — uses same util as card value
    const totalIncomeItems: BreakdownItem[] = incomeUtils
      .getCurrentMonthIncome(activeIncome)
      .map((inc) => ({
        name: inc.client,
        entityId: inc.id,
        entityType: "income" as const,
        isSuspended: incomeUtils.isIncomeSuspended(inc),
        suspendedUntil: inc.suspendedUntil,
        sourceProvider: inc.sourceProvider,
        sourceDocumentId: inc.sourceDocumentId,
        sourceDocumentType: inc.sourceDocumentType,
        sourceClientId: inc.sourceClientId,
        // The balance is what counts toward the card; the gross value and what
        // has been collected ride along so the row still reconciles against
        // Account Center.
        amount: incomeUtils.calculateOwed(inc),
        grossAmount: incomeUtils.getInvoiceTotal(inc),
        paidToDate: Number(inc.paidToDate) || 0,
        date: inc.expectedByDate,
        isReceived: Boolean(inc.receivedDate),
      }));

    const currentYearExpensesItems: BreakdownItem[] = expenses
      .filter((exp) => {
        const dueDate = new Date(exp.dueDate);
        return dueDate >= currentYearStart && dueDate <= currentYearEnd;
      })
      .map((exp) => ({
        name: exp.name,
        amount: -Math.abs(Number(exp.amount)),
        date: exp.dueDate,
      }));

    const currentYearIncomeItems: BreakdownItem[] = activeIncome
      .filter((inc) => {
        const dueDate = new Date(inc.expectedByDate);
        return dueDate >= currentYearStart && dueDate <= currentYearEnd;
      })
      .map((inc) => ({
        name: inc.client,
        entityId: inc.id,
        entityType: "income" as const,
        isSuspended: incomeUtils.isIncomeSuspended(inc),
        suspendedUntil: inc.suspendedUntil,
        sourceProvider: inc.sourceProvider,
        sourceDocumentId: inc.sourceDocumentId,
        sourceDocumentType: inc.sourceDocumentType,
        sourceClientId: inc.sourceClientId,
        amount: Number(inc.invoiceTotal),
        date: inc.expectedByDate,
      }));

    const pastYearExpensesItems: BreakdownItem[] = expenses
      .filter((exp) => {
        const dueDate = new Date(exp.dueDate);
        return dueDate >= pastYearStart && dueDate <= pastYearEnd;
      })
      .map((exp) => ({
        name: exp.name,
        amount: -Math.abs(Number(exp.amount)),
        date: exp.dueDate,
      }));

    const pastYearIncomeItems: BreakdownItem[] = activeIncome
      .filter((inc) => {
        const dueDate = new Date(inc.expectedByDate);
        return dueDate >= pastYearStart && dueDate <= pastYearEnd;
      })
      .map((inc) => ({
        name: inc.client,
        entityId: inc.id,
        entityType: "income" as const,
        isSuspended: incomeUtils.isIncomeSuspended(inc),
        suspendedUntil: inc.suspendedUntil,
        sourceProvider: inc.sourceProvider,
        sourceDocumentId: inc.sourceDocumentId,
        sourceDocumentType: inc.sourceDocumentType,
        sourceClientId: inc.sourceClientId,
        amount: Number(inc.invoiceTotal),
        date: inc.expectedByDate,
      }));

    // Total debt balance breakdown (uses filtered debts)
    const totalDebtBalanceItems: BreakdownItem[] = filteredDebts
      .filter((debt) => Number(debt.balance || 0) > 0)
      .map((debt) => ({
        name:
          debt.creditor && debt.account
            ? `${debt.creditor} - ${debt.account}`
            : debt.creditor || debt.account || "Unknown",
        amount: Number(debt.balance || 0),
        type: debt.type,
      }));

    // Credit utilization breakdown (credit cards only, uses filtered debts)
    const creditUtilizationItems: BreakdownItem[] = filteredDebts
      .filter((debt) => debt.type === "Credit Card")
      .map((debt) => ({
        name:
          debt.creditor && debt.account
            ? `${debt.creditor} - ${debt.account}`
            : debt.creditor || debt.account || "Unknown",
        amount: Number(debt.balance || 0),
        type: `Limit: ${expenseUtils.formatCurrency(Number(debt.creditLimit || 0))}`,
      }));

    // Credit utilization details (with usage, limit, and utilization %)
    const creditUtilizationDetails: CreditUtilizationItem[] = filteredDebts
      .filter((debt) => debt.type === "Credit Card")
      .map((debt) => {
        const balance = Math.abs(Number(debt.balance || 0));
        const limit = Number(debt.creditLimit || 0);
        const utilization = limit > 0 ? Math.round((balance / limit) * 100) : 0;
        return {
          name:
            debt.creditor && debt.account
              ? `${debt.creditor} - ${debt.account}`
              : debt.creditor || debt.account || "Unknown",
          balance,
          creditLimit: limit,
          utilization,
        };
      })
      .sort((a, b) => b.utilization - a.utilization); // Sort by highest utilization first

    // Total paydown amount breakdown (uses filtered debts, only paydownAmount)
    const totalPaydownItems: BreakdownItem[] = filteredDebts
      .filter((debt) => Number(debt.paydownAmount || 0) !== 0)
      .map((debt) => ({
        name:
          debt.creditor && debt.account
            ? `${debt.creditor} - ${debt.account}`
            : debt.creditor || debt.account || "Unknown",
        amount: Math.abs(Number(debt.paydownAmount || 0)),
        type: debt.type,
      }));

    // Credit cards breakdown (uses filtered debts)
    const creditCardsItems: BreakdownItem[] = filteredDebts
      .filter((debt) => debt.type === "Credit Card")
      .map((debt) => ({
        name:
          debt.creditor && debt.account
            ? `${debt.creditor} - ${debt.account}`
            : debt.creditor || debt.account || "Unknown",
        amount: Number(debt.balance || 0),
      }));

    // Personal debts breakdown (Personal Loans and Other only)
    const personalDebtsItems: BreakdownItem[] = filteredDebts
      .filter((debt) => {
        const type = debt.type?.toLowerCase() || "";
        return (
          (type === "personal loan" || type === "other") &&
          Number(debt.balance || 0) !== 0
        );
      })
      .map((debt) => ({
        name:
          debt.creditor && debt.account
            ? `${debt.creditor} - ${debt.account}`
            : debt.creditor || debt.account || "Unknown",
        amount: Number(debt.balance || 0),
        type: debt.type,
      }));

    // Business debts breakdown
    const businessDebtsItems: BreakdownItem[] = filteredDebts
      .filter(
        (debt) =>
          debt.type?.toLowerCase().includes("business") &&
          Number(debt.balance || 0) !== 0
      )
      .map((debt) => ({
        name:
          debt.creditor && debt.account
            ? `${debt.creditor} - ${debt.account}`
            : debt.creditor || debt.account || "Unknown",
        amount: Number(debt.balance || 0),
        type: debt.type,
      }));

    // Upcoming expenses breakdown (within 30 days)
    const upcomingExpensesItems: BreakdownItem[] = upcomingExpenses.map(
      (exp) => ({
        name: exp.name,
        amount: -Math.abs(Number(exp.amount)),
        date: exp.dueDate,
      })
    );
    const nextTenDayExpensesItems: BreakdownItem[] = nextTenDayExpenses.map(
      (exp) => ({
        name: exp.name,
        amount: -Math.abs(Number(exp.amount)),
        date: exp.dueDate,
      })
    );
    const nextSevenDayExpensesItems: BreakdownItem[] = nextSevenDayExpenses.map(
      (exp) => ({
        name: exp.name,
        amount: -Math.abs(Number(exp.amount)),
        date: exp.dueDate,
      })
    );

    // Upcoming income breakdown (within 30 days)
    const upcomingIncomeItems: BreakdownItem[] = upcomingIncome.map((inc) => ({
      name: inc.client,
      sourceProvider: inc.sourceProvider,
      sourceDocumentId: inc.sourceDocumentId,
      sourceDocumentType: inc.sourceDocumentType,
      sourceClientId: inc.sourceClientId,
      amount: Number(inc.invoiceTotal) - Number(inc.paidToDate || 0),
      date: inc.expectedByDate,
    }));
    const nextTenDayIncomeItems: BreakdownItem[] = nextTenDayIncome.map(
      (inc) => ({
        name: inc.client,
        entityId: inc.id,
        entityType: "income" as const,
        isSuspended: incomeUtils.isIncomeSuspended(inc),
        suspendedUntil: inc.suspendedUntil,
        sourceProvider: inc.sourceProvider,
        sourceDocumentId: inc.sourceDocumentId,
        sourceDocumentType: inc.sourceDocumentType,
        sourceClientId: inc.sourceClientId,
        amount: Number(inc.invoiceTotal) - Number(inc.paidToDate || 0),
        date: inc.expectedByDate,
      })
    );
    const nextSevenDayIncomeItems: BreakdownItem[] = nextSevenDayIncome.map(
      (inc) => ({
        name: inc.client,
        entityId: inc.id,
        entityType: "income" as const,
        isSuspended: incomeUtils.isIncomeSuspended(inc),
        suspendedUntil: inc.suspendedUntil,
        sourceProvider: inc.sourceProvider,
        sourceDocumentId: inc.sourceDocumentId,
        sourceDocumentType: inc.sourceDocumentType,
        sourceClientId: inc.sourceClientId,
        amount: Number(inc.invoiceTotal) - Number(inc.paidToDate || 0),
        date: inc.expectedByDate,
      })
    );

    // Upcoming debt payments breakdown (within 30 days)
    const upcomingDebtPaymentsItems: BreakdownItem[] = upcomingDebtPayments.map(
      (debt) => ({
        name:
          debt.creditor && debt.account
            ? `${debt.creditor} - ${debt.account}`
            : debt.creditor || debt.account || "Unknown",
        amount: Number(debt.paydownAmount || debt.maxMonthlyPayment || 0),
        date: debt.dueDate,
      })
    );
    const nextTenDayDebtPaymentsItems: BreakdownItem[] =
      nextTenDayDebtPayments.map((debt) => ({
        name:
          debt.creditor && debt.account
            ? `${debt.creditor} - ${debt.account}`
            : debt.creditor || debt.account || "Unknown",
        amount: Number(debt.paydownAmount || debt.maxMonthlyPayment || 0),
        date: debt.dueDate,
      }));
    const nextSevenDayDebtPaymentsItems: BreakdownItem[] =
      nextSevenDayDebtPayments.map((debt) => ({
        name:
          debt.creditor && debt.account
            ? `${debt.creditor} - ${debt.account}`
            : debt.creditor || debt.account || "Unknown",
        amount: Number(debt.paydownAmount || debt.maxMonthlyPayment || 0),
        date: debt.dueDate,
      }));

    // Next month expenses breakdown
    const nextMonthExpensesItems: BreakdownItem[] = nextMonthExpenses.map(
      (exp) => ({
        name: exp.name,
        amount: -Math.abs(Number(exp.amount)),
        date: exp.dueDate,
      })
    );

    // Next month income breakdown
    const nextMonthIncomeItems: BreakdownItem[] = nextMonthIncome.map(
      (inc) => ({
        name: inc.client,
        entityId: inc.id,
        entityType: "income" as const,
        isSuspended: incomeUtils.isIncomeSuspended(inc),
        suspendedUntil: inc.suspendedUntil,
        sourceProvider: inc.sourceProvider,
        sourceDocumentId: inc.sourceDocumentId,
        sourceDocumentType: inc.sourceDocumentType,
        sourceClientId: inc.sourceClientId,
        amount: Number(inc.invoiceTotal) - Number(inc.paidToDate || 0),
        date: inc.expectedByDate,
      })
    );

    // Next month debt payments breakdown
    const nextMonthDebtPaymentsItems: BreakdownItem[] =
      nextMonthDebtPayments.map((debt) => ({
        name:
          debt.creditor && debt.account
            ? `${debt.creditor} - ${debt.account}`
            : debt.creditor || debt.account || "Unknown",
        amount: Number(debt.paydownAmount || debt.maxMonthlyPayment || 0),
        date: debt.dueDate,
      }));

    return {
      remainingExpenses: remainingExpensesItems,
      remainingIncome: withBalance(remainingIncomeItems),
      monthlyDebtPayments: monthlyDebtPaymentsItems,
      totalExpenses: totalExpensesItems,
      totalIncome: withBalance(totalIncomeItems),
      // Same records as totalIncome, but valued at the gross invoice total
      // (what was expected) rather than the outstanding balance — backs the
      // "Income › Expected" card.
      expectedIncome: withBalance(
        totalIncomeItems.map((item) => ({
          ...item,
          amount:
            typeof item.grossAmount === "number"
              ? item.grossAmount
              : item.amount,
        }))
      ),
      currentYearExpenses: currentYearExpensesItems,
      currentYearIncome: currentYearIncomeItems,
      pastYearExpenses: pastYearExpensesItems,
      pastYearIncome: pastYearIncomeItems,
      totalDebtBalance: totalDebtBalanceItems,
      creditUtilization: creditUtilizationItems,
      creditUtilizationDetails: creditUtilizationDetails,
      totalPaydown: totalPaydownItems,
      creditCards: creditCardsItems,
      personalDebts: personalDebtsItems,
      businessDebts: businessDebtsItems,
      upcomingExpenses: upcomingExpensesItems,
      upcomingIncome: withBalance(upcomingIncomeItems),
      upcomingDebtPayments: upcomingDebtPaymentsItems,
      nextTenDayExpenses: nextTenDayExpensesItems,
      nextTenDayIncome: withBalance(nextTenDayIncomeItems),
      nextTenDayDebtPayments: nextTenDayDebtPaymentsItems,
      nextSevenDayExpenses: nextSevenDayExpensesItems,
      nextSevenDayIncome: withBalance(nextSevenDayIncomeItems),
      nextSevenDayDebtPayments: nextSevenDayDebtPaymentsItems,
      nextMonthExpenses: nextMonthExpensesItems,
      nextMonthIncome: withBalance(nextMonthIncomeItems),
      nextMonthDebtPayments: nextMonthDebtPaymentsItems,
    };
  }, [
    expenses,
    income,
    filteredDebts,
    currentYear,
    pastYear,
    upcomingExpenses,
    upcomingIncome,
    upcomingDebtPayments,
    nextTenDayExpenses,
    nextTenDayIncome,
    nextTenDayDebtPayments,
    nextSevenDayExpenses,
    nextSevenDayIncome,
    nextSevenDayDebtPayments,
    nextMonthExpenses,
    nextMonthIncome,
    nextMonthDebtPayments,
  ]);

  useEffect(() => {
    console.log("[Dashboard] Annual totals", {
      currentYear,
      currentYearIncome: breakdowns.currentYearIncome.reduce(
        (sum, item) => sum + item.amount,
        0
      ),
      currentYearExpenses: breakdowns.currentYearExpenses.reduce(
        (sum, item) => sum + item.amount,
        0
      ),
      pastYear,
      pastYearIncome: breakdowns.pastYearIncome.reduce(
        (sum, item) => sum + item.amount,
        0
      ),
      pastYearExpenses: breakdowns.pastYearExpenses.reduce(
        (sum, item) => sum + item.amount,
        0
      ),
    });
  }, [breakdowns, currentYear, pastYear]);

  // Use sections as groups for backward compatibility with visibility panel
  const groups = sections;

  // Find which section contains a card
  const findSectionContainingCard = useCallback(
    (cardId: string): DashboardSection | undefined => {
      return sections.find((section) =>
        section.cards.some((card) => card.id === cardId)
      );
    },
    [sections]
  );

  // Drag-and-drop handlers
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      setActiveId(active.id);

      // Determine if we're dragging a section or a card
      const isSection = sections.some((section) => section.id === active.id);
      setActiveDragType(isSection ? "section" : "card");
      console.log(
        "[Dashboard DnD] Drag start:",
        active.id,
        isSection ? "section" : "card"
      );
    },
    [sections]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const activeId = active.id as string;
      const overId = over.id as string;

      // Only handle card-to-different-section moves
      const activeSection = findSectionContainingCard(activeId);
      const overSection = findSectionContainingCard(overId);

      // Check if over is a section itself (for dropping card into empty section)
      const isOverSection = sections.some((s) => s.id === overId);

      if (activeDragType === "card") {
        // Card being dragged over another section
        if (isOverSection && activeSection && activeSection.id !== overId) {
          setSections((prev) => {
            const activeCard = activeSection.cards.find(
              (c) => c.id === activeId
            );
            if (!activeCard) return prev;

            return prev.map((section) => {
              if (section.id === activeSection.id) {
                return {
                  ...section,
                  cards: section.cards.filter((c) => c.id !== activeId),
                };
              }
              if (section.id === overId) {
                return {
                  ...section,
                  cards: [...section.cards, activeCard],
                };
              }
              return section;
            });
          });
          console.log("[Dashboard DnD] Card moved to section:", overId);
        }
        // Card being dragged over another card in a different section
        else if (
          overSection &&
          activeSection &&
          activeSection.id !== overSection.id
        ) {
          setSections((prev) => {
            const activeCard = activeSection.cards.find(
              (c) => c.id === activeId
            );
            if (!activeCard) return prev;

            const overIndex = overSection.cards.findIndex(
              (c) => c.id === overId
            );

            return prev.map((section) => {
              if (section.id === activeSection.id) {
                return {
                  ...section,
                  cards: section.cards.filter((c) => c.id !== activeId),
                };
              }
              if (section.id === overSection.id) {
                const newCards = [...section.cards];
                newCards.splice(overIndex, 0, activeCard);
                return {
                  ...section,
                  cards: newCards,
                };
              }
              return section;
            });
          });
          console.log("[Dashboard DnD] Card moved between sections");
        }
      }
    },
    [activeDragType, findSectionContainingCard, sections]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);
      setActiveDragType(null);

      if (!over || active.id === over.id) return;

      const activeId = active.id as string;
      const overId = over.id as string;

      if (activeDragType === "section") {
        // Reorder sections
        const oldIndex = sections.findIndex((s) => s.id === activeId);
        const newIndex = sections.findIndex((s) => s.id === overId);

        if (oldIndex !== -1 && newIndex !== -1) {
          setSections((prev) => arrayMove(prev, oldIndex, newIndex));
          console.log(
            "[Dashboard DnD] Sections reordered:",
            oldIndex,
            "->",
            newIndex
          );
        }
      } else {
        // Reorder cards within the same section
        const section = findSectionContainingCard(activeId);
        const overInSameSection = section?.cards.some((c) => c.id === overId);

        if (section && overInSameSection) {
          const oldIndex = section.cards.findIndex((c) => c.id === activeId);
          const newIndex = section.cards.findIndex((c) => c.id === overId);

          if (oldIndex !== -1 && newIndex !== -1) {
            setSections((prev) =>
              prev.map((s) => {
                if (s.id === section.id) {
                  return {
                    ...s,
                    cards: arrayMove(s.cards, oldIndex, newIndex),
                  };
                }
                return s;
              })
            );
            console.log(
              "[Dashboard DnD] Cards reordered in section:",
              section.id
            );
          }
        }
      }
    },
    [activeDragType, findSectionContainingCard, sections]
  );

  // Section management handlers
  const handleAddSection = useCallback(() => {
    if (!newSectionName.trim()) return;

    const newSection: DashboardSection = {
      id: `section-${Date.now()}`,
      label: newSectionName.trim(),
      cards: [],
    };

    setSections((prev) => [...prev, newSection]);
    setNewSectionName("");
    setIsAddingSectionOpen(false);
    console.log("[Dashboard] Added new section:", newSection.label);
  }, [newSectionName]);

  const handleDeleteSection = useCallback(
    (sectionId: string) => {
      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;

      if (section.cards.length > 0) {
        // Move cards to first section or prompt user
        const confirmDelete = window.confirm(
          `Section "${section.label}" has ${section.cards.length} card(s). Delete anyway? Cards will be moved to the first section.`
        );
        if (!confirmDelete) return;

        setSections((prev) => {
          const cardsToMove = section.cards;
          return prev
            .filter((s) => s.id !== sectionId)
            .map((s, index) => {
              if (index === 0) {
                return { ...s, cards: [...s.cards, ...cardsToMove] };
              }
              return s;
            });
        });
      } else {
        setSections((prev) => prev.filter((s) => s.id !== sectionId));
      }
      console.log("[Dashboard] Deleted section:", sectionId);
    },
    [sections]
  );

  const handleEditSectionName = useCallback(
    (sectionId: string, newName: string) => {
      setSections((prev) =>
        prev.map((s) => (s.id === sectionId ? { ...s, label: newName } : s))
      );
      console.log("[Dashboard] Renamed section:", sectionId, "to", newName);
    },
    []
  );

  // Reset layout to default
  const handleResetLayout = useCallback(() => {
    const confirmReset = window.confirm(
      "Reset dashboard layout to default? This will undo all customizations."
    );
    if (confirmReset) {
      setSections(DEFAULT_SECTIONS);
      console.log("[Dashboard] Layout reset to default");
    }
  }, []);

  useLayoutEffect(() => {
    setPanelHeight(panelRef.current?.scrollHeight || 0);
  }, [sections.length]);

  useEffect(() => {
    hasMounted.current = true;
  }, []);

  const isGroupHidden = (groupId: string) =>
    Boolean(visibility.hiddenGroups[groupId]);
  const isCardHidden = (cardId: string) =>
    Boolean(visibility.hiddenCards[cardId]);

  const toggleGroupHidden = (groupId: string) => {
    setVisibility((prev) => ({
      ...prev,
      hiddenGroups: {
        ...prev.hiddenGroups,
        [groupId]: !prev.hiddenGroups[groupId],
      },
    }));
  };

  const toggleCardHidden = (cardId: string) => {
    setVisibility((prev) => ({
      ...prev,
      hiddenCards: {
        ...prev.hiddenCards,
        [cardId]: !prev.hiddenCards[cardId],
      },
    }));
  };

  // Card content renderer - returns the appropriate content for each card ID
  const renderCardContent = (cardId: string): React.ReactNode => {
    const isDarkMode = theme.palette.mode === "dark";
    const cardColor = getCardColor(cardId);

    switch (cardId) {
      case "remaining-expenses":
        return (
          <MetricCard
            title="Expenses › Remaining"
            value={`-${expenseUtils.formatCurrency(remainingMonthExpenses)}`}
            color={cardColor}
            borderColor={cardColor}
            breakdownItems={breakdowns.remainingExpenses}
            breakdownTitle="Expenses › Remaining"
          />
        );
      case "remaining-income":
        return (
          <MetricCard
            title="Income › Remaining"
            value={incomeUtils.formatCurrency(remainingMonthIncome)}
            color={cardColor}
            borderColor={cardColor}
            breakdownItems={breakdowns.remainingIncome}
            breakdownTitle="Income › Remaining"
            breakdownItemActions={incomeBreakdownActions}
            footerHint="Gross"
            footerHintTooltip="Gross income is your total income before expenses or taxes."
          />
        );
      case "monthly-debt-payments":
        return (
          <MetricCard
            title="Debts › Payments"
            value={`-${expenseUtils.formatCurrency(totalMonthlyDebtPayments)}`}
            color={cardColor}
            borderColor={cardColor}
            breakdownItems={breakdowns.monthlyDebtPayments}
            breakdownTitle="Debts › Payments"
          />
        );
      case "net-cash-flow":
        return (
          <MetricCard
            title="Income › Net"
            value={expenseUtils.formatCurrency(projectedCashFlow)}
            valueLabel="Projected"
            secondaryLabel="Actual"
            secondaryValue={expenseUtils.formatCurrency(actualCashFlow)}
            splitValues
            compactValues
            color={
              coloringEnabled
                ? projectedCashFlow >= 0
                  ? "#38A169"
                  : "#E53E3E"
                : "#718096"
            }
            borderColor={cardColor}
            subtitle="Income - Expenses"
            breakdownItems={[
              ...breakdowns.totalIncome.map((item) => ({
                ...item,
              })),
              ...breakdowns.totalExpenses.map((item) => ({
                ...item,
                amount: -item.amount,
              })),
            ]}
            breakdownTitle="Cash Flow Breakdown"
          />
        );
      case "total-expenses":
        return (
          <MetricCard
            title="Expenses › Total"
            value={`-${expenseUtils.formatCurrency(totalMonthExpenses)}`}
            color={cardColor}
            borderColor={cardColor}
            breakdownItems={breakdowns.totalExpenses}
            breakdownTitle="Expenses › Total"
          />
        );
      case "total-income":
        return (
          <MetricCard
            title="Income › Expected"
            value={incomeUtils.formatCurrency(expectedMonthIncome)}
            subtitle={`Income › Received  ${incomeUtils.formatCurrency(
              receivedMonthIncome
            )}`}
            color={cardColor}
            borderColor={cardColor}
            breakdownItems={breakdowns.expectedIncome}
            breakdownTitle="Income › Expected"
            breakdownItemActions={incomeBreakdownActions}
            footerHint="Gross · Received = money in this month, excl. transfers"
            footerHintTooltip="Expected is the gross amount you invoiced or projected for this month. Received is money that actually landed in your bank this month (from transactions), excluding internal transfers."
          />
        );
      case "checking-balance": {
        const isNegative = totalCheckingBalance < 0;
        const displayColor = coloringEnabled
          ? isNegative
            ? "#E53E3E"
            : "#38A169"
          : "#718096";
        return (
          <BalanceMetricCard
            title="Balance › Checking"
            value={`${isNegative ? "-" : ""}${expenseUtils.formatCurrency(Math.abs(totalCheckingBalance))}`}
            color={displayColor}
            borderColor={displayColor}
            accounts={checkingAccounts.map((account) => ({
              name: account.name,
              officialName: account.officialName,
              currentBalance: account.balance,
              availableBalance: account.available,
              lastSynced: account.lastSynced,
              institution: account.institution,
              mask: account.mask,
              itemId: account.itemId,
              isPlaidConnected: account.isPlaidConnected,
            }))}
            breakdownTitle="Checking Accounts"
            onSync={handleSyncBankAccounts}
          />
        );
      }
      case "savings-balance": {
        const isNegative = totalSavingsBalance < 0;
        const displayColor = coloringEnabled
          ? isNegative
            ? "#E53E3E"
            : "#38A169"
          : "#718096";
        return (
          <BalanceMetricCard
            title="Balance › Savings"
            value={`${isNegative ? "-" : ""}${expenseUtils.formatCurrency(Math.abs(totalSavingsBalance))}`}
            color={displayColor}
            borderColor={displayColor}
            accounts={savingsAccounts.map((account) => ({
              name: account.name,
              officialName: account.officialName,
              currentBalance: account.balance,
              availableBalance: account.available,
              lastSynced: account.lastSynced,
              institution: account.institution,
              mask: account.mask,
              itemId: account.itemId,
              isPlaidConnected: account.isPlaidConnected,
            }))}
            breakdownTitle="Savings Accounts"
            onSync={handleSyncBankAccounts}
          />
        );
      }
      case "current-year-income":
        return (
          <MetricCard
            title={`Income › ${currentYear}`}
            value={incomeUtils.formatCurrency(
              breakdowns.currentYearIncome.reduce(
                (sum, item) => sum + item.amount,
                0
              )
            )}
            color={cardColor}
            borderColor={cardColor}
            breakdownItems={breakdowns.currentYearIncome}
            breakdownTitle={`Income › ${currentYear}`}
            compactValues
          />
        );
      case "projected-income-2026": {
        const momLabel =
          incomeProjectionMoM === null
            ? "MoM: —"
            : `MoM: ${incomeProjectionMoM >= 0 ? "+" : ""}${incomeProjectionMoM.toFixed(1)}%`;
        return (
          <MetricCard
            title="Income › Projected › 2026"
            value={incomeUtils.formatCurrency(projectedIncome2026)}
            color={cardColor}
            borderColor={cardColor}
            subtitle={momLabel}
            compactValues
            onClick={() => setProjectedIncomeModalOpen(true)}
          />
        );
      }
      case "current-year-expenses":
        return (
          <MetricCard
            title={`Expenses › ${currentYear}`}
            value={`-${expenseUtils.formatCurrency(
              breakdowns.currentYearExpenses.reduce(
                (sum, item) => sum + item.amount,
                0
              )
            )}`}
            color={cardColor}
            borderColor={cardColor}
            breakdownItems={breakdowns.currentYearExpenses}
            breakdownTitle={`Expenses › ${currentYear}`}
            compactValues
          />
        );
      case "past-year-income":
        return (
          <MetricCard
            title={`Income › ${pastYear}`}
            value={incomeUtils.formatCurrency(
              breakdowns.pastYearIncome.reduce(
                (sum, item) => sum + item.amount,
                0
              )
            )}
            color={cardColor}
            borderColor={cardColor}
            breakdownItems={breakdowns.pastYearIncome}
            breakdownTitle={`Income › ${pastYear}`}
            compactValues
          />
        );
      case "past-year-expenses":
        return (
          <MetricCard
            title={`Expenses › ${pastYear}`}
            value={`-${expenseUtils.formatCurrency(
              breakdowns.pastYearExpenses.reduce(
                (sum, item) => sum + item.amount,
                0
              )
            )}`}
            color={cardColor}
            borderColor={cardColor}
            breakdownItems={breakdowns.pastYearExpenses}
            breakdownTitle={`Expenses › ${pastYear}`}
            compactValues
          />
        );
      case "income-history":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: incomeHistoryData.items,
                title: `Income (Last ${incomeHistoryDays} Days)`,
              })
            }
            sx={{
              borderLeft: "4px solid #38A169",
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3), 0 1px 3px 0 rgba(0, 0, 0, 0.2)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.08)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Income History
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: "#38A169",
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                {incomeUtils.formatCurrency(incomeHistoryData.total)}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? "#6b7280" : "#9ca3af",
                  fontSize: "0.75rem",
                  display: "block",
                }}
              >
                {incomeHistoryData.count} paid invoices
              </Typography>
              <TextField
                type="number"
                size="small"
                value={incomeHistoryDays}
                onChange={(e) => {
                  e.stopPropagation();
                  const val = parseInt(e.target.value, 10);
                  if (val > 0 && val <= 365) {
                    setIncomeHistoryDays(val);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                InputProps={{
                  endAdornment: (
                    <Typography
                      variant="caption"
                      sx={{
                        color: isDarkMode ? "#6b7280" : "#9ca3af",
                        ml: 0.5,
                      }}
                    >
                      days
                    </Typography>
                  ),
                }}
                sx={{
                  marginTop: 1,
                  width: "100%",
                  "& .MuiInputBase-root": { height: 32, fontSize: "0.8rem" },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isDarkMode
                      ? "rgba(255,255,255,0.2)"
                      : "rgba(0,0,0,0.2)",
                  },
                }}
              />
            </CardContent>
          </Card>
        );
      case "credit-utilization":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.creditCards,
                title: "Credit › Utilization",
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3), 0 1px 3px 0 rgba(0, 0, 0, 0.2)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.08)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Credit › Utilization
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                {creditUtilization}%
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? "#6b7280" : "#9ca3af",
                  fontSize: "0.75rem",
                  marginTop: 0.5,
                  display: "block",
                }}
              >
                Available:{" "}
                {expenseUtils.formatCurrency(
                  totalCreditLimit - totalCreditBalance
                )}
              </Typography>
            </CardContent>
          </Card>
        );
      case "total-paydown":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.totalPaydown,
                title: "Total Debts",
              })
            }
            sx={{
              borderLeft: "4px solid #E53E3E",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Total Debts
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#E53E3E" }}
              >
                -{expenseUtils.formatCurrency(totalPaydownAmount)}
              </Typography>
            </CardContent>
          </Card>
        );
      case "personal-debts":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.personalDebts,
                title: "Debts › Personal",
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3), 0 1px 3px 0 rgba(0, 0, 0, 0.2)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.08)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Debts › Personal
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                -
                {expenseUtils.formatCurrency(
                  Math.abs(totalPersonalDebtBalance)
                )}
              </Typography>
              {topPersonalDebtGroups.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    borderTop: "1px solid",
                    borderColor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.1)",
                    pt: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDarkMode ? "#6b7280" : "#9ca3af",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    Highest Balances
                  </Typography>
                  {topPersonalDebtGroups.map((group, index) => (
                    <DebtGroupHoverReveal
                      key={index}
                      group={group}
                      isDarkMode={isDarkMode}
                      formatCurrency={(amount) =>
                        expenseUtils.formatCurrency(amount)
                      }
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        );
      case "business-debts":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.businessDebts,
                title: "Debts › Business",
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3), 0 1px 3px 0 rgba(0, 0, 0, 0.2)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.08)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Debts › Business
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                -
                {expenseUtils.formatCurrency(
                  Math.abs(totalBusinessDebtBalance)
                )}
              </Typography>
              {topBusinessDebts.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    borderTop: "1px solid",
                    borderColor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.1)",
                    pt: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDarkMode ? "#6b7280" : "#9ca3af",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    Highest Balances
                  </Typography>
                  {groupDebtsByCreditor(topBusinessDebts).map(
                    (group, index) => (
                      <DebtGroupHoverReveal
                        key={index}
                        group={group}
                        isDarkMode={isDarkMode}
                        formatCurrency={(amount) =>
                          expenseUtils.formatCurrency(amount)
                        }
                      />
                    )
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        );
      case "credit-cards":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.creditCards,
                title: "Credit › Cards",
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3), 0 1px 3px 0 rgba(0, 0, 0, 0.2)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.08)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Credit › Cards
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                -{expenseUtils.formatCurrency(totalCreditBalance)}
              </Typography>
              {topCreditCards.length > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    borderTop: "1px solid",
                    borderColor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.1)",
                    pt: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDarkMode ? "#6b7280" : "#9ca3af",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      fontSize: "0.65rem",
                    }}
                  >
                    Highest Balances
                  </Typography>
                  {groupDebtsByCreditor(topCreditCards).map((group, index) => (
                    <DebtGroupHoverReveal
                      key={index}
                      group={group}
                      isDarkMode={isDarkMode}
                      formatCurrency={(amount) =>
                        expenseUtils.formatCurrency(amount)
                      }
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        );
      case "expected-expenses":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.upcomingExpenses,
                title: "Expenses › Expected",
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Expenses › Expected
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                -
                {expenseUtils.formatCurrency(
                  upcomingExpenses.reduce((sum, exp) => sum + exp.amount, 0)
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#6b7280" : "#9ca3af" }}
              >
                {upcomingExpenses.length} items
              </Typography>
            </CardContent>
          </Card>
        );
      case "expected-income":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.upcomingIncome,
                title: "Income › Expected",
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Income › Expected
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                {incomeUtils.formatCurrency(
                  upcomingIncome.reduce(
                    (sum, inc) => sum + (inc.invoiceTotal - inc.paidToDate),
                    0
                  )
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#6b7280" : "#9ca3af" }}
              >
                {upcomingIncome.length} items
              </Typography>
            </CardContent>
          </Card>
        );
      case "upcoming-debt-payments":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.upcomingDebtPayments,
                title: "Debts › Upcoming",
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Debts › Upcoming
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                -
                {expenseUtils.formatCurrency(
                  upcomingDebtPayments.reduce(
                    (sum, debt) =>
                      sum +
                      Number(debt.paydownAmount || debt.maxMonthlyPayment || 0),
                    0
                  )
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#6b7280" : "#9ca3af" }}
              >
                {upcomingDebtPayments.length} payments due
              </Typography>
            </CardContent>
          </Card>
        );
      case "next-10-expenses":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.nextTenDayExpenses,
                title: "Expenses › Within 10 Days",
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Expenses › Within 10 Days
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                -
                {expenseUtils.formatCurrency(
                  nextTenDayExpenses.reduce((sum, exp) => sum + exp.amount, 0)
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#6b7280" : "#9ca3af" }}
              >
                {nextTenDayExpenses.length} items
              </Typography>
            </CardContent>
          </Card>
        );
      case "next-10-income":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.nextTenDayIncome,
                title: "Income › Within 10 Days",
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Income › Within 10 Days
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                {incomeUtils.formatCurrency(
                  nextTenDayIncome.reduce(
                    (sum, inc) => sum + (inc.invoiceTotal - inc.paidToDate),
                    0
                  )
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#6b7280" : "#9ca3af" }}
              >
                {nextTenDayIncome.length} items
              </Typography>
            </CardContent>
          </Card>
        );
      case "next-10-debt-payments":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.nextTenDayDebtPayments,
                title: "Debts › Within 10 Days",
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Debts › Within 10 Days
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                -
                {expenseUtils.formatCurrency(
                  nextTenDayDebtPayments.reduce(
                    (sum, debt) =>
                      sum +
                      Number(debt.paydownAmount || debt.maxMonthlyPayment || 0),
                    0
                  )
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#6b7280" : "#9ca3af" }}
              >
                {nextTenDayDebtPayments.length} items
              </Typography>
            </CardContent>
          </Card>
        );
      case "next-7-expenses":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.nextSevenDayExpenses,
                title: "Expenses › Within 7 Days",
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Expenses › Within 7 Days
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                -
                {expenseUtils.formatCurrency(
                  nextSevenDayExpenses.reduce((sum, exp) => sum + exp.amount, 0)
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#6b7280" : "#9ca3af" }}
              >
                {nextSevenDayExpenses.length} items
              </Typography>
            </CardContent>
          </Card>
        );
      case "next-7-income":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.nextSevenDayIncome,
                title: "Income › Within 7 Days",
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Income › Within 7 Days
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                {incomeUtils.formatCurrency(
                  nextSevenDayIncome.reduce(
                    (sum, inc) => sum + (inc.invoiceTotal - inc.paidToDate),
                    0
                  )
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#6b7280" : "#9ca3af" }}
              >
                {nextSevenDayIncome.length} items
              </Typography>
            </CardContent>
          </Card>
        );
      case "next-7-debt-payments":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.nextSevenDayDebtPayments,
                title: "Debts › Within 7 Days",
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Debts › Within 7 Days
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                -
                {expenseUtils.formatCurrency(
                  nextSevenDayDebtPayments.reduce(
                    (sum, debt) =>
                      sum +
                      Number(debt.paydownAmount || debt.maxMonthlyPayment || 0),
                    0
                  )
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#6b7280" : "#9ca3af" }}
              >
                {nextSevenDayDebtPayments.length} items
              </Typography>
            </CardContent>
          </Card>
        );
      case "next-month-expenses":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.nextMonthExpenses,
                title: `Expenses › ${nextMonthName}`,
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Expenses › {nextMonthName}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                -
                {expenseUtils.formatCurrency(
                  nextMonthExpenses.reduce(
                    (sum, exp) => sum + Number(exp.amount),
                    0
                  )
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#6b7280" : "#9ca3af" }}
              >
                {nextMonthExpenses.length} expenses
              </Typography>
            </CardContent>
          </Card>
        );
      case "next-month-income":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.nextMonthIncome,
                title: `Income › ${nextMonthName}`,
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Income › {nextMonthName}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                {incomeUtils.formatCurrency(
                  nextMonthIncome.reduce(
                    (sum, inc) =>
                      sum +
                      (Number(inc.invoiceTotal) - Number(inc.paidToDate || 0)),
                    0
                  )
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#6b7280" : "#9ca3af" }}
              >
                {nextMonthIncome.length} invoices
              </Typography>
            </CardContent>
          </Card>
        );
      case "next-month-debt-payments":
        return (
          <Card
            onClick={() =>
              setOpenBreakdownModal({
                items: breakdowns.nextMonthDebtPayments,
                title: `Debts › ${nextMonthName}`,
              })
            }
            sx={{
              borderLeft: `4px solid ${cardColor}`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              backgroundImage: isDarkMode
                ? "linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.03))"
                : "none",
              boxShadow: isDarkMode
                ? "0 4px 6px 0 rgba(0, 0, 0, 0.3)"
                : "0 4px 6px 0 rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: isDarkMode
                  ? "0 8px 12px 0 rgba(0, 0, 0, 0.4)"
                  : "0 8px 12px 0 rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "#9ca3af" : "#6b7280",
                  fontWeight: 500,
                  letterSpacing: "0.025em",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  marginBottom: 1,
                }}
              >
                Debts › {nextMonthName}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: cardColor,
                  fontWeight: 700,
                  fontSize: "1.875rem",
                  lineHeight: 1.2,
                }}
              >
                -
                {expenseUtils.formatCurrency(
                  nextMonthDebtPayments.reduce(
                    (sum, debt) =>
                      sum +
                      Number(debt.paydownAmount || debt.maxMonthlyPayment || 0),
                    0
                  )
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#6b7280" : "#9ca3af" }}
              >
                {nextMonthDebtPayments.length} payments due
              </Typography>
            </CardContent>
          </Card>
        );
      default:
        return (
          <Card
            sx={{
              backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
              height: "100%",
            }}
          >
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                Unknown card: {cardId}
              </Typography>
            </CardContent>
          </Card>
        );
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: { xs: 2, md: 3 },
        minHeight: "100vh",
        position: "relative",
      }}
    >
      {/* Dashboard Tabs */}
      {dashboardsLoading ? (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          {[0, 1, 2].map((index) => (
            <Skeleton
              key={`dashboard-tab-skeleton-${index}`}
              variant="rounded"
              width={160}
              height={40}
            />
          ))}
        </Box>
      ) : (
        dashboards.length > 0 && (
          <DashboardTabs
            dashboards={dashboards}
            activeDashboardId={activeDashboardId}
            onTabChange={handleDashboardTabChange}
            onCreateNew={() => setCreateDashboardOpen(true)}
            onRename={(dashboard) => setRenameDashboard(dashboard)}
            onSetDefault={handleSetDefaultDashboard}
            onDelete={handleDeleteDashboard}
          />
        )
      )}

      {/* Create Dashboard Dialog */}
      <CreateDashboardDialog
        open={createDashboardOpen}
        onClose={() => {
          setCreateDashboardOpen(false);
          setDashboardError(null);
        }}
        onCreate={handleCreateDashboard}
        currentDashboard={activeDashboard}
        defaultLayout={DEFAULT_SECTIONS}
        error={dashboardError}
        mode="create"
      />

      {/* Rename Dashboard Dialog */}
      <CreateDashboardDialog
        open={Boolean(renameDashboard)}
        onClose={() => {
          setRenameDashboard(null);
          setDashboardError(null);
        }}
        onCreate={(data) => handleRenameDashboard(data)}
        currentDashboard={activeDashboard}
        defaultLayout={DEFAULT_SECTIONS}
        error={dashboardError}
        mode="rename"
        initialName={renameDashboard?.name || ""}
        initialDescription={renameDashboard?.description || ""}
      />

      {/* Share Dashboard Modal */}
      <ShareDashboardModal
        open={Boolean(shareDashboard)}
        dashboard={shareDashboard}
        onClose={() => setShareDashboard(null)}
        captureSnapshotData={captureSnapshotData}
      />

      <Dialog
        open={projectedIncomeModalOpen}
        onClose={() => setProjectedIncomeModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Income › Projected › 2026</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Based on {projectionDayOfYear} days of income data in 2026.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Avg daily: {incomeUtils.formatCurrency(projectionAvgDailyIncome)}{" "}
              • Projected: {incomeUtils.formatCurrency(projectedIncome2026)}
            </Typography>
          </Box>

          {projectionIncomeByPayee.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No income entries found for 2026.
            </Typography>
          ) : (
            projectionIncomeByPayee.map((group) => (
              <Accordion key={group.payee} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      pr: 1,
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>
                      {group.payee}
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {incomeUtils.formatCurrency(group.total)}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {group.items.map((item) => (
                      <Box
                        key={item.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {item.expectedByDate
                            ? formatDateUTC(item.expectedByDate)
                            : "—"}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {incomeUtils.formatCurrency(
                            incomeUtils.getInvoiceTotal(item)
                          )}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProjectedIncomeModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          position: "fixed",
          top: { xs: 64, sm: 70 },
          right: { xs: 12, md: 24 },
          zIndex: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          transform: `translateY(${isVisibilityOpen ? 0 : -panelHeight}px)`,
          transition:
            hasMounted.current && isVisibilityOpen
              ? "transform 220ms ease"
              : "none",
        }}
      >
        <Box
          sx={{
            overflow: "hidden",
          }}
        >
          <Box
            ref={panelRef}
            sx={{
              padding: 1.5,
              borderRadius: "0 0 0 12px",
              backgroundColor: theme.palette.background.paper,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 0 16px rgba(255, 255, 255, 0.12)"
                  : "0 12px 24px rgba(15, 23, 42, 0.12)",
              border: `1px solid ${theme.palette.divider}`,
              borderBottom: `1px solid ${theme.palette.divider}`,
              minWidth: 240,
            }}
          >
            {/* Edit Mode Controls */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                marginBottom: 2,
                paddingBottom: 1.5,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDraggingEnabled}
                    onChange={(e) => setIsDraggingEnabled(e.target.checked)}
                    size="small"
                    sx={{
                      color: theme.palette.text.secondary,
                      "&.Mui-checked": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Edit Layout Mode
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={coloringEnabled}
                    onChange={(e) => handleColoringToggle(e.target.checked)}
                    size="small"
                    sx={{
                      color: theme.palette.text.secondary,
                      "&.Mui-checked": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Card Colors
                  </Typography>
                }
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleResetLayout}
                sx={{ fontSize: "0.7rem", padding: "2px 8px" }}
              >
                Reset Layout
              </Button>
            </Box>

            <FormGroup
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                gap: 2,
                alignItems: "flex-start",
                overflowX: "auto",
                paddingBottom: 0.5,
              }}
            >
              {groups.map((group) => (
                <Box key={group.id} sx={{ minWidth: 180 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!isGroupHidden(group.id)}
                        onChange={() => toggleGroupHidden(group.id)}
                        size="small"
                        sx={{
                          color: theme.palette.text.secondary,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {group.label}
                      </Typography>
                    }
                  />
                  <Box sx={{ marginLeft: 2 }}>
                    {group.cards.map((card) => (
                      <FormControlLabel
                        key={card.id}
                        control={
                          <Checkbox
                            checked={!isCardHidden(card.id)}
                            onChange={() => toggleCardHidden(card.id)}
                            size="small"
                            disabled={isGroupHidden(group.id)}
                            sx={{
                              color: theme.palette.text.secondary,
                              "&.Mui-checked": {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                        }
                        label={
                          <Typography variant="caption">
                            {card.label}
                          </Typography>
                        }
                        sx={{ display: "flex", alignItems: "center" }}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </FormGroup>
          </Box>
        </Box>
        <Box
          onClick={() => setIsVisibilityOpen((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setIsVisibilityOpen((prev) => !prev);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Toggle dashboard card visibility"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            justifyContent: "center",
            width: 38,
            height: 38,
            borderRadius: "0 0 12px 12px",
            cursor: "pointer",
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 8px 18px rgba(0, 0, 0, 0.5)"
                : "0 6px 16px rgba(15, 23, 42, 0.12)",
            border: `1px solid ${theme.palette.divider}`,
            borderTop: 0,
            position: "relative",
          }}
        >
          <Tune
            sx={{
              fontSize: 20,
              transition: "transform 0.2s ease",
              transform: isVisibilityOpen ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />
        </Box>
      </Box>
      {isLoading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {loadingSections.map((section) => {
            const visibleCards = section.cards || [];
            const columnCount = getSectionColumnCount(
              section.id,
              visibleCards.length
            );
            const isAnnualSection = section.id === "annual-overview";
            const gridSize = Math.floor(12 / Math.max(columnCount, 1));
            const annualColumns = isAnnualSection
              ? { xs: 12, md: columnCount, lg: columnCount }
              : undefined;
            const annualItemSize = isAnnualSection
              ? ({ xs: 12, md: 1, lg: 1 } as const)
              : gridSize;

            return (
              <Box key={`loading-section-${section.id}`}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Skeleton variant="text" width={220} height={28} />
                  <Skeleton variant="rounded" width={90} height={28} />
                </Box>
                <Grid container spacing={2} columns={annualColumns}>
                  {visibleCards.map((card) => (
                    <Grid
                      key={`loading-card-${section.id}-${card.id}`}
                      size={annualItemSize}
                    >
                      <Card
                        sx={{
                          height: "100%",
                          backgroundColor: isDarkMode ? "#1a1a1a" : "#ffffff",
                        }}
                      >
                        <CardContent>
                          <Skeleton variant="text" width="45%" height={20} />
                          <Skeleton variant="text" width="70%" height={36} />
                          <Skeleton variant="text" width="30%" height={16} />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          })}
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sections.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {sections.map((section) => {
              // Skip hidden sections
              if (isGroupHidden(section.id)) return null;

              // Filter visible cards
              const visibleCards = section.cards.filter(
                (card) => !isCardHidden(card.id)
              );
              if (visibleCards.length === 0 && !isDraggingEnabled) return null;

              // Get column count from user preference or calculate default
              const columnCount = getSectionColumnCount(
                section.id,
                visibleCards.length
              );
              const isAnnualSection = section.id === "annual-overview";
              const gridSize = Math.floor(12 / columnCount);
              const annualColumns = isAnnualSection
                ? { xs: 12, md: columnCount, lg: columnCount }
                : undefined;
              const annualItemSize = isAnnualSection
                ? ({ xs: 12, md: 1, lg: 1 } as const)
                : gridSize;

              // All sections show their headers
              const showHeader = true;

              return (
                <SortableSection
                  key={section.id}
                  section={section}
                  isDraggingEnabled={isDraggingEnabled}
                  onDeleteSection={handleDeleteSection}
                  onEditSectionName={handleEditSectionName}
                  showHeader={showHeader}
                  columnCount={columnCount}
                  onColumnChange={handleSectionColumnChange}
                >
                  <SortableContext
                    items={section.cards.map((c) => c.id)}
                    strategy={horizontalListSortingStrategy}
                  >
                    <Grid
                      container
                      spacing={2}
                      columns={annualColumns}
                      sx={{ marginBottom: 3, alignItems: "stretch" }}
                    >
                      {visibleCards.map((card) => (
                        <Grid
                          key={card.id}
                          size={annualItemSize}
                          sx={{ display: "flex" }}
                        >
                          <SortableCardWrapper
                            id={card.id}
                            isDraggingEnabled={isDraggingEnabled}
                            cardColor={getCardColor(card.id)}
                            onColorChange={handleCardColorChange}
                          >
                            {renderCardContent(card.id)}
                          </SortableCardWrapper>
                        </Grid>
                      ))}
                      {visibleCards.length === 0 && isDraggingEnabled && (
                        <Grid size={12}>
                          <Box
                            sx={{
                              padding: 3,
                              border: `2px dashed ${theme.palette.divider}`,
                              borderRadius: 2,
                              textAlign: "center",
                              color: theme.palette.text.secondary,
                            }}
                          >
                            <Typography variant="body2">
                              Drag cards here
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </SortableContext>
                </SortableSection>
              );
            })}
          </SortableContext>

          {/* Add Section Button */}
          {isDraggingEnabled && (
            <Box sx={{ marginTop: 3 }}>
              {isAddingSectionOpen ? (
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    padding: 2,
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Section name"
                    value={newSectionName}
                    onChange={(e) => setNewSectionName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleAddSection();
                      if (e.key === "Escape") {
                        setIsAddingSectionOpen(false);
                        setNewSectionName("");
                      }
                    }}
                    autoFocus
                    sx={{ flex: 1 }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddSection}
                    disabled={!newSectionName.trim()}
                  >
                    Add
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setIsAddingSectionOpen(false);
                      setNewSectionName("");
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setIsAddingSectionOpen(true)}
                  sx={{
                    width: "100%",
                    padding: 2,
                    borderStyle: "dashed",
                    borderWidth: 2,
                  }}
                >
                  Add New Section
                </Button>
              )}
            </Box>
          )}

          {/* Drag Overlay */}
          <DragOverlay>
            {activeId ? (
              <Box
                sx={{
                  opacity: 0.8,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
                  borderRadius: 2,
                  backgroundColor: theme.palette.background.paper,
                  padding: 2,
                }}
              >
                <Typography variant="body2">
                  {activeDragType === "section"
                    ? sections.find((s) => s.id === activeId)?.label
                    : sections
                        .flatMap((s) => s.cards)
                        .find((c) => c.id === activeId)?.label}
                </Typography>
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Breakdown Modal */}
      {openBreakdownModal && (
        <BreakdownModal
          open={!!openBreakdownModal}
          onClose={() => setOpenBreakdownModal(null)}
          items={openBreakdownModal.items}
          title={openBreakdownModal.title}
          formatCurrency={expenseUtils.formatCurrency}
          itemActions={incomeBreakdownActions}
        />
      )}

      {/* Edit a Focus: Finance-native income record from a breakdown row */}
      <Dialog
        open={Boolean(incomeToEdit)}
        onClose={() => setIncomeToEdit(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Edit Income
          <IconButton size="small" onClick={() => setIncomeToEdit(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {incomeToEdit && (
            <IncomeForm
              isEditing
              incomeToEdit={incomeToEdit}
              onUpdateIncome={handleUpdateIncome}
              onAddIncome={() => {}}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={incomeToDelete.length > 0}
        onClose={() => setIncomeToDelete([])}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {incomeToDelete.length > 1
            ? `Delete ${incomeToDelete.length} income items?`
            : "Delete income?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {incomeToDelete.length > 1
              ? "These will be permanently removed:"
              : "This will be permanently removed:"}
          </Typography>
          {incomeToDelete.map((inc) => (
            <Typography key={inc.id} variant="body2" sx={{ opacity: 0.8 }}>
              • {inc.client} —{" "}
              {incomeUtils.formatCurrency(Number(inc.invoiceTotal || 0))}
              {inc.expectedByDate
                ? ` (${formatDateUTC(inc.expectedByDate)})`
                : ""}
            </Typography>
          ))}
        </DialogContent>
        {incomeActionError && (
          <DialogContent sx={{ pt: 0 }}>
            <Typography variant="body2" sx={{ color: "#E53E3E" }}>
              {incomeActionError}
            </Typography>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setIncomeToDelete([])}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDeleteIncome}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suspend / resume */}
      <Dialog
        open={incomeToSuspend.length > 0}
        onClose={() => setIncomeToSuspend([])}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {incomeToSuspend.length > 1
            ? `Suspend ${incomeToSuspend.length} income items`
            : "Suspend income"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Suspended income stays listed but is excluded from Income totals.
          </Typography>
          <TextField
            type="date"
            fullWidth
            size="small"
            label="Suspend until"
            InputLabelProps={{ shrink: true }}
            value={suspendUntil}
            onChange={(e) => setSuspendUntil(e.target.value)}
          />
        </DialogContent>
        {incomeActionError && (
          <DialogContent sx={{ pt: 0 }}>
            <Typography variant="body2" sx={{ color: "#E53E3E" }}>
              {incomeActionError}
            </Typography>
          </DialogContent>
        )}
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button onClick={() => setIncomeToSuspend([])}>Cancel</Button>
          {incomeToSuspend.some((inc) => inc.isSuspended) && (
            <Button
              color="success"
              onClick={() => handleApplySuspension("resume")}
            >
              Resume now
            </Button>
          )}
          <Button onClick={() => handleApplySuspension("indefinite")}>
            Suspend indefinitely
          </Button>
          <Button
            variant="contained"
            disabled={!suspendUntil}
            onClick={() => handleApplySuspension("until")}
          >
            Suspend until date
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardPage;
